import type { APPagination } from "@/api/accounts-payable/accountsPayable.types";
import type { FilterEntry } from "@/utils/buildFilterByParam";

// ── Enums (string codes shared with the backend) ────────────────────────────

export const PROCUREMENT_CATEGORIES = ["PRICING", "PURCHASE_ITEMS", "RENTALS", "TRAVEL"] as const;
export type ProcurementCategory = (typeof PROCUREMENT_CATEGORIES)[number];

export const PROCUREMENT_CATEGORY_LABELS: Record<ProcurementCategory, string> = {
  PRICING: "Pricing",
  PURCHASE_ITEMS: "Purchase Items",
  RENTALS: "Rentals",
  TRAVEL: "Travel",
};

/**
 * SERV-2632. The nine lanes a request can be raised through. The queue's Procurement Type column
 * and its filter chip both read this; `PROCUREMENT_CATEGORY_LABELS` above is still what the four
 * stored categories are called, but it is too coarse for the queue.
 */
export const PROCUREMENT_LANES = [
  "INBOUND_PHONE_SALE",
  "OUTBOUND_PHONE_SALE",
  "STORAGE",
  "EQUIPMENT_RENTAL",
  "VEHICLE_BOOKING",
  "TRAVEL",
  "PRICING",
  /* Last, matching the backend: the broadest item-type set, so a specific lane wins a mixed basket. */
  "ONLINE_PURCHASE",
] as const;
export type ProcurementLane = (typeof PROCUREMENT_LANES)[number];

/*
 * These are the labels the request-type picker uses, verbatim (procurementOptions.ts requestTypes).
 * Not a fresh set of names: an officer who raised an "Online Purchase" has to find the word "Online
 * Purchase" in the queue, and the first version of this took its wording from the Overview page's
 * prose headings instead, so the same lane was called two different things depending on the screen.
 *
 * There is no Labor lane, though the picker once had one. Its item type, UNIT, is also what the
 * Online Purchase link list writes for a "Unit" link, and Online Purchase is live while Labor was
 * cut in SERV-2626 - so treating UNIT as labor labelled live online purchases "Labor". A labor
 * sub-request raised before that cut now reads "Online Purchase" instead, which is the less
 * misleading of the two errors. See LANE_ITEM_TYPES in the backend's procurement/types.ts.
 */
export const PROCUREMENT_LANE_LABELS: Record<ProcurementLane, string> = {
  INBOUND_PHONE_SALE: "Inbound Phone Sale",
  OUTBOUND_PHONE_SALE: "Outbound Phone Sale",
  STORAGE: "Storage Unit",
  EQUIPMENT_RENTAL: "Equipment Rental",
  VEHICLE_BOOKING: "Vehicle Booking",
  TRAVEL: "Flights & Hotels Booking",
  PRICING: "Request for Pricing",
  ONLINE_PURCHASE: "Online Purchase",
};

/**
 * SERV-3158: lanes whose lines are never ordered, so they never take a PO number. Mirrors the
 * backend's PO_INELIGIBLE_ITEM_TYPES, which answers 409 LANE_NOT_ORDERABLE for these.
 */
export const PO_INELIGIBLE_LANES: readonly ProcurementLane[] = ["PRICING", "STORAGE", "TRAVEL"];

export const laneTakesPo = (lane: ProcurementLane): boolean => !PO_INELIGIBLE_LANES.includes(lane);

export const PROCUREMENT_ITEM_TYPES = [
  "PHONE_SALE",
  "PARTS",
  "MATERIALS",
  "CONSUMABLES",
  "UNIT",
  "TOOLS",
  "EQUIPMENT",
  "VEHICLE_RENTAL",
  "HOTEL_BOOKING",
  "FLIGHT_BOOKING",
  "TRANSPORTATION_RENTAL",
  "PRICING",
  "STORAGE_UNIT",
] as const;
export type ProcurementItemType = (typeof PROCUREMENT_ITEM_TYPES)[number];

export const PROCUREMENT_ITEM_TYPE_LABELS: Record<ProcurementItemType, string> = {
  PHONE_SALE: "Phone Sale",
  PARTS: "Parts",
  MATERIALS: "Materials",
  CONSUMABLES: "Consumables",
  UNIT: "Unit",
  TOOLS: "Tools",
  EQUIPMENT: "Equipment",
  VEHICLE_RENTAL: "Vehicle Rental",
  HOTEL_BOOKING: "Hotel Booking",
  FLIGHT_BOOKING: "Flight Booking",
  TRANSPORTATION_RENTAL: "Transportation Rental",
  PRICING: "Pricing",
  STORAGE_UNIT: "Storage Unit",
};

/**
 * SERV-2855 D3: the item types offered when adding to an existing request are scoped to that
 * request's CURRENT lane - no cross-lane picker. Mirrors the backend's own LANE_ITEM_TYPES
 * (onengine-backend src/modules/procurement/types.ts:201), which the add-items guard enforces;
 * kept here rather than re-derived so the two never drift silently apart.
 */
export const LANE_ITEM_TYPES: Record<ProcurementLane, ProcurementItemType[]> = {
  INBOUND_PHONE_SALE: ["PHONE_SALE"],
  OUTBOUND_PHONE_SALE: ["PHONE_SALE"],
  STORAGE: ["STORAGE_UNIT"],
  EQUIPMENT_RENTAL: ["EQUIPMENT", "VEHICLE_RENTAL"],
  VEHICLE_BOOKING: ["TRANSPORTATION_RENTAL"],
  TRAVEL: ["HOTEL_BOOKING", "FLIGHT_BOOKING"],
  PRICING: ["PRICING"],
  ONLINE_PURCHASE: ["PARTS", "MATERIALS", "CONSUMABLES", "TOOLS", "UNIT"],
};

export const PROCUREMENT_REQUEST_STATUSES = [
  "PENDING",
  "IN_PROGRESS",
  "COMPLETED",
  "DELAYED",
  "CANCELLED",
  /*
   * SERV-3353: procurement replied to the request's creator and waits on them. The status before
   * it is held server-side and restored by "Send back to procurement".
   */
  "RETURNED_TO_DISPATCHER",
] as const;
export type ProcurementRequestStatus = (typeof PROCUREMENT_REQUEST_STATUSES)[number];

export const PROCUREMENT_APPROVAL_STATUSES = ["PENDING_APPROVAL", "APPROVED", "REJECTED"] as const;
export type ProcurementApprovalStatus = (typeof PROCUREMENT_APPROVAL_STATUSES)[number];

/**
 * A single status computed server-side from the request's status, approval state and item
 * states combined - the database has no column that means any of these on its own. Replaces the
 * design queue's demo-only kebab-case vocabulary (`pending-lead-approval`, `approved-sourcing`, ...).
 */
export const PROCUREMENT_DERIVED_STATUSES = [
  "PENDING_APPROVAL",
  "BLOCKED",
  "SOURCING",
  "IN_PROGRESS",
  "ORDERED",
  "ETA_CONFIRMED",
  "SHIPPED",
  "DELIVERED",
  "ON_SITE",
  "COMPLETED",
  "CANCELLED",
  "RETURNED_TO_DISPATCHER",
] as const;
export type ProcurementDerivedStatus = (typeof PROCUREMENT_DERIVED_STATUSES)[number];

export const PROCUREMENT_DERIVED_STATUS_LABELS: Record<ProcurementDerivedStatus, string> = {
  PENDING_APPROVAL: "Pending Approval",
  BLOCKED: "Blocked",
  SOURCING: "Sourcing",
  IN_PROGRESS: "In Progress",
  ORDERED: "Ordered",
  ETA_CONFIRMED: "ETA Confirmed",
  SHIPPED: "Shipped",
  DELIVERED: "Delivered",
  ON_SITE: "On Site",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  RETURNED_TO_DISPATCHER: "Returned to dispatcher",
};

export const PROCUREMENT_ITEM_STATUSES = [
  "REQUESTED",
  "IN_PROGRESS",
  /**
   * SERV-2508: the officer is on the call to the supplier about this one line. Outbound phone sale
   * only - the backend refuses it on any other lane with a 422, so it is never a status the rest of
   * the app has to render a control for. Mirrors PROCUREMENT_ITEM_STATUS in the backend's
   * procurement/types.ts, where the reasoning for a per-line status lives.
   */
  "ON_PHONE",
  "PICKED_UP",
  "DELIVERED",
  /*
   * SERV-2973: the two states only a rental reaches. ON_HIRE is the kit out with the clock running;
   * RETURNED is the meter stopped with the branch's final invoice not in yet, which is deliberately
   * short of COMPLETED because a deposit is usually still held against it.
   * Listed here so the union matches what the API can actually return - without them a rental line
   * typechecked as an impossible status and rendered its raw enum name.
   */
  "ON_HIRE",
  "RETURNED",
  /* SERV-3440: travel and storage confirms. Each is accepted only on its own item type. */
  "CHECKED_IN",
  "CHECKED_OUT",
  "FLOWN",
  "COLLECTED",
  "MOVED_IN",
  /*
   * SERV-3497: a shipped line between ordering and arrival. ORDERED is how an outbound call ends
   * now (it used to be COMPLETED, which read as done while the parts were still on the way);
   * ETA_CONFIRMED is either goods lane once the supplier confirms the ETA. Receiving moves both on.
   */
  "ORDERED",
  "ETA_CONFIRMED",
  "COMPLETED",
  "CANCELLED",
] as const;
export type ProcurementItemStatus = (typeof PROCUREMENT_ITEM_STATUSES)[number];

/**
 * The queue's four tiles, sent as `filterBy.queueView` - a single value, not an array, since the
 * tiles are mutually exclusive. Mirrors PROCUREMENT_REQUEST_QUEUE_VIEWS in the backend's
 * procurement/types.ts.
 */
export const PROCUREMENT_REQUEST_QUEUE_VIEWS = [
  "awaiting",
  "inProgress",
  "open",
  "assignedToMe",
] as const;
export type ProcurementRequestQueueView = (typeof PROCUREMENT_REQUEST_QUEUE_VIEWS)[number];

/**
 * SERV-2859 D3: the four live/closed stages plus the three terminal failures a return can end on
 * instead (a vendor refusal at the counter, a lost parcel, a lapsed/abandoned return). Order matters
 * for the live ladder in ReturnLifecycle/ProcurementItemDrawer - the three failures are appended
 * after it rather than interleaved, so `[...RETURN_STATUSES].indexOf` on a live value is unaffected.
 */
export const RETURN_STATUSES = [
  "RETURN_PENDING",
  "SHIPPING_LABEL_RECEIVED",
  "SHIPPED",
  "REFUNDED",
  "REFUSED",
  "LOST",
  "CANCELLED",
] as const;
export type ReturnStatus = (typeof RETURN_STATUSES)[number];

/** SERV-2859 D3/AC1.2: the live/closed ladder, unchanged, plus the three failure labels. */
export const RETURN_STATUS_LABELS: Record<ReturnStatus, string> = {
  RETURN_PENDING: "Return Pending",
  SHIPPING_LABEL_RECEIVED: "Shipping Label Received",
  SHIPPED: "Shipped",
  REFUNDED: "Refunded",
  REFUSED: "Refused",
  LOST: "Lost in transit",
  CANCELLED: "Return cancelled",
};

/**
 * SERV-2859 D3/A6: the LIVE half of the return-state split the queue badge and filter both read.
 * FAILED reuses `PROCUREMENT_RETURN_TERMINAL_FAILURES` below rather than a second array, mirroring
 * the backend's single shared constant (types.ts A6) - REFUNDED is in neither set.
 */
export const PROCUREMENT_RETURN_LIVE_STATUSES: ReturnStatus[] = [
  "RETURN_PENDING",
  "SHIPPING_LABEL_RECEIVED",
  "SHIPPED",
];

export const PROCUREMENT_RETURN_TERMINAL_FAILURES: ReturnStatus[] = [
  "REFUSED",
  "LOST",
  "CANCELLED",
];

/**
 * SERV-2859 AC2.1: the queue's "Returns" filter chip and the queue badge's return-state split.
 *
 * SERV-2885 adds SETTLED (returnStatus REFUNDED), which the returns screen needs and the queue
 * badge deliberately does not show: a credited return is closed and carries no badge. It is a
 * filter value on both screens all the same, because "show me the ones we actually got money back
 * on" is the question a settled return exists to answer - and before this ticket the returns screen
 * answered it by hiding those rows behind a checkbox.
 */
export const RETURN_STATES = ["LIVE", "FAILED", "SETTLED"] as const;
export type ProcurementReturnState = (typeof RETURN_STATES)[number];

export const RETURN_STATE_LABELS: Record<ProcurementReturnState, string> = {
  LIVE: "Live",
  FAILED: "Failed",
  SETTLED: "Settled",
};

/** ADDENDUM A1 - exact wire literals, mirrored verbatim from onengine-backend's types.ts. */
export const RETURN_QR_EMAIL_STATUSES = [
  "SENT",
  "FAILED",
  "SKIPPED_NO_SP_EMAIL",
  "SKIPPED_NO_QR",
] as const;
export type ReturnQrEmailStatus = (typeof RETURN_QR_EMAIL_STATUSES)[number];

export const RETURN_REASONS = [
  "WRONG_ITEM",
  "DAMAGED",
  "DEFECTIVE",
  "WRONG_QUANTITY",
  "NO_LONGER_NEEDED",
  "VENDOR_PRICING_ERROR",
  "PICKUP_ERROR",
  "OTHER",
] as const;
export type ReturnReason = (typeof RETURN_REASONS)[number];

export const RETURN_REASON_LABELS: Record<ReturnReason, string> = {
  WRONG_ITEM: "Wrong item received",
  DAMAGED: "Damaged on arrival",
  DEFECTIVE: "Defective / doesn't work",
  WRONG_QUANTITY: "Wrong quantity (over- or under-ship)",
  NO_LONGER_NEEDED: "No longer needed — scope changed",
  VENDOR_PRICING_ERROR: "Vendor pricing error / billing discrepancy",
  PICKUP_ERROR: "Pickup error — cashier handed over wrong product",
  OTHER: "Other",
};

/** A vendor refuses a damage or defect claim without evidence, so photos gate submission. */
export const RETURN_REASONS_REQUIRING_PHOTOS: ReturnReason[] = ["DAMAGED", "DEFECTIVE"];

export const RETURN_OUTCOMES = ["REPLACEMENT", "REFUND", "CREDIT"] as const;
export type ReturnOutcome = (typeof RETURN_OUTCOMES)[number];

export const RETURN_OUTCOME_LABELS: Record<ReturnOutcome, string> = {
  REPLACEMENT: "Replacement (same item)",
  REFUND: "Refund",
  CREDIT: "Credit on account",
};

export const formatReturnReason = (reason: string | null | undefined): string | null => {
  if (!reason) return null;
  return (RETURN_REASON_LABELS as Record<string, string | undefined>)[reason] ?? reason;
};

export const RETURN_METHODS = ["IN_STORE_RETURN", "SHIP_BACK"] as const;
export type ReturnMethod = (typeof RETURN_METHODS)[number];

export const RETURN_METHOD_LABELS: Record<ReturnMethod, string> = {
  IN_STORE_RETURN: "In-Store Return",
  SHIP_BACK: "Ship Back",
};

export const FULFILLMENT_METHODS = ["DELIVERY", "PICKUP", "NONE"] as const;
export type FulfillmentMethod = (typeof FULFILLMENT_METHODS)[number];

export const FULFILLMENT_METHOD_LABELS: Record<FulfillmentMethod, string> = {
  DELIVERY: "Delivery",
  PICKUP: "Pickup",
  NONE: "None",
};

export const PAYMENT_STATUSES = ["UNPAID", "PAID"] as const;
export type ProcurementPaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_STATUS_LABELS: Record<ProcurementPaymentStatus, string> = {
  UNPAID: "Unpaid",
  PAID: "Paid",
};

export const CARD_BRANDS = ["VISA", "MASTERCARD", "AMEX", "OTHER"] as const;
export type CardBrand = (typeof CARD_BRANDS)[number];

export const CARD_BRAND_LABELS: Record<CardBrand, string> = {
  VISA: "Visa",
  MASTERCARD: "Mastercard",
  AMEX: "Amex",
  OTHER: "Other",
};

/**
 * "Visa ••4242". Known brands get their friendly label; an unrecognised one falls back to the raw
 * value the API sent, mirroring paymentTermsLabel's fallback below rather than rendering
 * "undefined" or throwing.
 */
export const formatVendorAccountCardLabel = (card: { brand: string; last4: string }): string =>
  `${(CARD_BRAND_LABELS as Record<string, string | undefined>)[card.brand] ?? card.brand} ••${card.last4}`;

// ── Field limits (mirror the backend column precision) ──────────────────────

/** Money columns are Decimal(10,2); above this the write fails with a numeric overflow. */
export const PROCUREMENT_MAX_MONEY = 99999999.99;
/** Quantity columns are Int; above this the write fails with an out-of-range error. */
export const PROCUREMENT_MAX_QUANTITY = 2147483647;

export const PROCUREMENT_MAX_MONEY_MESSAGE = "Amount must be 99,999,999.99 or less";
export const PROCUREMENT_MAX_QUANTITY_MESSAGE = "Quantity must be 2,147,483,647 or less";

/** Format the autoincrement request number for display, e.g. ("BSM", 12) -> "BSM-00012". */
/**
 * SERV-2670: a child of a supplier split shares its parent's number and is told apart by a dash
 * suffix, so PR-01234 has children PR-01234-1, PR-01234-2 and PR-01234-3. Sharing the number is
 * what makes the suffix mean "part of 1234" rather than naming three unrelated requests.
 *
 * childSequence is null on a parent and on any request that was never split, which renders exactly
 * as before.
 */
export const formatProcurementRequestNumber = (
  entityAbbreviation: string | null | undefined,
  requestNumber: number,
  childSequence?: number | null,
): string =>
  `${entityAbbreviation ?? "PR"}-${String(requestNumber).padStart(5, "0")}` +
  (childSequence != null ? `-${childSequence}` : "");

/**
 * SERV-3169: how a pricing request is named on screen. A linked one is its queue ticket, so the
 * queue, the breadcrumb and the pricing list show one id for one record. Its own counter shares the
 * entity prefix, so showing it named a different queue ticket. An unlinked one keeps its own number.
 */
export const formatPricingRequestNumber = (
  item: Pick<
    PricingRequestListItem,
    "entityAbbreviation" | "requestNumber" | "queueRequestNumber" | "queueChildSequence"
  >,
): string =>
  item.queueRequestNumber != null
    ? formatProcurementRequestNumber(
        item.entityAbbreviation,
        item.queueRequestNumber,
        item.queueChildSequence,
      )
    : formatProcurementRequestNumber(item.entityAbbreviation, item.requestNumber);

/**
 * Reads a request number back out of a formatted id. Stripping every non-digit, which is what this
 * replaced, turned the child id PR-01234-2 into 12342 - a different and possibly real request,
 * silently. The number is the first digit run after the prefix; anything after the next dash is the
 * child sequence and is deliberately not part of it.
 */
export const parseProcurementRequestNumber = (
  formatted: string,
): { requestNumber: number; childSequence: number | null } => {
  const match = /^[A-Za-z]*-?(\d+)(?:-(\d+))?$/.exec(formatted.trim());
  if (!match) return { requestNumber: 0, childSequence: null };
  return {
    requestNumber: Number(match[1]),
    childSequence: match[2] === undefined ? null : Number(match[2]),
  };
};

// ── JSON column shapes ───────────────────────────────────────────────────────

export type ProcurementFulfillmentInfo = {
  recipientUserId?: string;
  recipientName?: string;
  technicianUserId?: string;
  technicianName?: string;
  technicianPhone?: string;
  deliveryLocation?: string;
  deliveryLocationLabel?: string;
  /**
   * SERV-2959. Where a pickup is being collected from, when the dispatcher typed it rather than
   * picking a branch record. The mirror of `deliveryLocation` above, which the delivery half of the
   * same step has always had - the pickup half appended its answer to the request's notes instead,
   * so the drawer's "Pickup Location" row rendered a dash for an address that had been entered.
   */
  pickupLocation?: string;
  pickupLocationLabel?: string;
  /**
   * SERV-3352. The split address behind `pickupLocation` / `deliveryLocation`, written only for the
   * active method. Absent on every request created before it, so read defensively.
   */
  pickupStreet?: string | null;
  pickupCity?: string | null;
  pickupState?: string | null;
  pickupZip?: string | null;
  deliveryStreet?: string | null;
  deliveryCity?: string | null;
  deliveryState?: string | null;
  deliveryZip?: string | null;
  notes?: string;
  /**
   * SERV-2868 (site 2638): whether the SP has been texted this item's pickup details.
   * `fulfillmentInfo` merges server-side, same as `attributes` - a partial write preserves the
   * keys it does not name. Every write of this field still reads the column first and spreads it
   * back defensively, so a client that predates that merge behaviour cannot lose the sibling
   * fields above either.
   */
  texted?: boolean;
};

export const PHONE_SALE_DIRECTIONS = ["INBOUND", "OUTBOUND"] as const;
export type PhoneSaleDirection = (typeof PHONE_SALE_DIRECTIONS)[number];

export type PhoneSaleAttributes = ItemDimensionAttributes & {
  /** The item name only; the unit is its own key. */
  partsList: string;
  /**
   * SERV-3359. One of the item-unit codes (ITEM, BOX, PACK, LINEAR_FEET, SQUARE_FEET). Typed as a
   * plain string because rows saved before the dropdown have none, and test data carries free text
   * ("each"); readers go through `itemUnitLabel`, which shows anything else as Item.
   */
  unit?: string;
  direction?: PhoneSaleDirection;
  /**
   * SERV-2508, outbound only. Who is driving to the supplier, when the request is a PICKUP. Absent
   * on a DELIVERY, where the supplier ships and nobody is dispatched, and absent on every sale
   * captured before this.
   */
  serviceProvider?: string;
  /**
   * SERV-2508. How the quotation was paid. Free-form rather than an enum because the option list is
   * an operational choice (company card, bank transfer, credit account, petty cash) that finance
   * changes without a deploy, and nothing branches on the value - it is reported, not acted on.
   */
  paymentMethod?: string;

  isCounterPickup?: boolean;
  quotationS3Key?: string;
  /** SERV-3352, inbound only. Snapshot taken at create; absent on every earlier request. */
  clientName?: string;
  /** SERV-3360: written on every lane now, as the names in `teamLeaders` joined ", ". */
  teamLeaderName?: string;
  teamLeaders?: ProcurementTeamLeader[];
  supplier?: string;
  supplierAddress?: string;
  supplierPhone?: string;
  /**
   * SERV-2959. The store location the dispatcher typed, when no branch record was picked. Written
   * only when `supplierLocationId` is absent - a linked branch is the better answer and travels as
   * an id. Before this it was appended to the request's notes as a sentence, where nothing could
   * render it as a field.
   */
  supplierLocation?: string;
  subtotal?: number;
  discount?: number;
  tax?: number;
  shipping?: number;
  total?: number;
  /**
   * SERV-3023, outbound only. When the goods bought on the call actually moved - collected on a
   * PICKUP, received on a DELIVERY - and who recorded it. A stamp rather than a status, because a
   * completed phone-sale line is terminal in the item transition map.
   */
  handedOverAt?: string;
  handedOverBy?: string;
  /**
   * SERV-3356, outbound only. A payment or quotation link the dispatcher attached, stored on the
   * request's first line like the quotation totals. The backend does not validate attribute
   * values, so a stored value may not be http(s); null means it was explicitly cleared.
   */
  quotationLink?: string | null;
  /**
   * SERV-2980, outbound only. Set on a line the officer added with Add Item after the request was
   * raised, so "What dispatch submitted" does not present it as the dispatcher's. Absent on every
   * line the create wizard wrote, and on lines added before this existed.
   */
  addedAfterRaise?: boolean;
  /**
   * SERV-2980, outbound only, on the first line. What dispatch submitted about the supplier and
   * its quotation, kept apart from the keys above that Complete sale overwrites with the call.
   * See OUTBOUND_DISPATCH_KEY in design/createRequestPayload.ts.
   */
  dispatch?: {
    supplier?: string;
    supplierAddress?: string;
    supplierPhone?: string;
    subtotal?: number;
    discount?: number;
    tax?: number;
    shipping?: number;
    total?: number;
  };
};

export type EquipmentAttributes = {
  attributeValue?: string;
  attributeName?: string;
  equipmentId?: number;
  equipmentName?: string;
  /**
   * hours / days / weeks / months. Rentals are priced by the unit rather than by elapsed time, so
   * two weeks and fourteen days are different commercial asks even though the dates match. Carried
   * here as well as in the request's notes text, because the quote loop (SERV-2678) has to read it
   * rather than parse a sentence. Absent on every rental created before SERV-2656.
   */
  durationUnit?: string;
  /** What the officer reads out when they ring the supplier. Absent on rentals raised before this,
   * and often absent after: the dispatcher fills it when they have the plate, not always. */
  /** SERV-2657. ISO instants; the unit says whether the time of day is meaningful. */
  rentalStartAt?: string;
  rentalEndAt?: string;
  make?: string;
  model?: string;
  /**
   * SERV-2832: the catalogued model's own display name (`EquipmentCatalogueRow.modelName`),
   * distinct from `model`, which holds the "Model Number" spec value the picker also copies into
   * the free-text Model input.
   */
  catalogueModelName?: string;
  /**
   * SERV-2832: the picked catalogue row's full spec sheet. Same key the online-purchase and
   * pricing lanes write (see `PricingAttributes.selectedSpecs`), so a later stage need not know
   * which lane produced the item.
   */
  selectedSpecs?: Record<string, string>;
  /**
   * The picked catalogue row's own source bullets (`EquipmentCatalogueRow.highlights`) and where it
   * was scraped from - most rows carry these instead of, or alongside, `selectedSpecs`, since only a
   * minority of scraped pages publish a spec table at all.
   */
  catalogueHighlights?: string[];
  catalogueSourceName?: string;
  catalogueSourceUrl?: string;
  /**
   * SERV-2868 AC4/AC5: what the branch actually gave back when the officer reserved with the
   * provider. Flat, not nested under a `reservation` key - `mergeAttributes` (service.ts:252-264)
   * is a one-level spread, so a nested object would silently drop its own sibling keys on the next
   * partial write. `poNumber` itself goes to the item's own column, not here.
   */
  reservationBranch?: string;
  reservationAccessories?: string[];
  reservationDriverVerified?: boolean | null;
  /** SERV-2868 AC6: what the provider said when the officer called to stop the meter. */
  offRentAt?: string;
  offRentCallLog?: string;
  /**
   * SERV-3073: the logistics answers the rental company's work-order form asks for, filled by the
   * dispatcher when the request is raised. Flat keys (`mergeAttributes` is a one-level spread) and
   * named for the form, so a later filled-in PDF can render straight from them. Every one is
   * absent on a rental raised before this ticket, and most are absent after: none is required.
   */
  facilityName?: string;
  facilityAddress?: string;
  facilityContactName?: string;
  facilityContactPhone?: string;
  rentalCompanyContactName?: string;
  rentalCompanyContactPhone?: string;
  tradeRequired?: string;
  /** Yes/No on the form; absent means the dispatcher did not say, which is not the same as No. */
  powerAvailable?: boolean;
  voltagePhase?: string;
  specialEquipment?: string;
  equipmentContactName?: string;
  equipmentContactPhone?: string;
  serviceRequestDetails?: string;
  /**
   * SERV-3100: the rest of the rental company's work-order form (the Sunbelt sheet). Same rules as
   * the SERV-3073 block above: flat, optional, absent on every older row.
   *
   * `requestedTime` is the time half of the form's "Requested Date/Time" as `HH:mm`, local to the
   * site; the date half is `rentalStartAt`. `tonnage` is free text because the catalogue states it
   * in whatever unit the page did ("3 t", "5,500 lb", "12 ton"). `equipmentStatus` is the form's
   * On Site / Need to Be Delivered box, which is not the same question as how it is fulfilled.
   */
  requestedTime?: string;
  tonnage?: string;
  equipmentStatus?: RentalEquipmentStatus;
  equipmentPickupAddress?: string;
};

export type RentalEquipmentStatus = "ON_SITE" | "NEEDS_DELIVERY";

/**
 * SERV-2832: one row of `GET /procurement/equipment-catalogue`. `sizeBucket` and `imageUrl` may
 * come back as `""` per the contract; every other field is read defensively in
 * `procurement.equipmentCatalogue.ts` rather than trusted blind, since the endpoint is being
 * built in parallel with this lane.
 */
export type EquipmentCatalogueRow = {
  id: string;
  equipmentType: string;
  sizeBucket: string;
  modelName: string;
  specs: Record<string, string>;
  headlineSpecs: string[];
  /**
   * SERV-2832: the source's own bullet list, verbatim prose rather than key/value pairs. Populated
   * for the equipment categories with no spec table at all (`specs` and `headlineSpecs` empty for
   * those rows) - render as plain text, never split on punctuation to manufacture labels.
   */
  highlights: string[];
  imageUrl: string;
  sourceName: string;
  sourceUrl: string;
  capturedAt: string;
};

export type HotelBookingRoom = {
  technicianName?: string;
  companionFirstName?: string;
  companionLastName?: string;
};

export type HotelBookingAttributes = {
  workOrderLocation?: string;
  checkIn?: string;
  checkOut?: string;
  estimatedCost?: number;
  rooms: HotelBookingRoom[];
  /**
   * SERV-3167: the stay as booked. The all-in total lives on the item's own `anticipatedCost`
   * column; these are the parts of it, flat because the backend merges attributes one level deep.
   * `roomCount` is separate from `rooms`, which is the per-room occupant list. All absent on
   * bookings recorded before SERV-3167, and nullable because a cleared field is sent as null.
   */
  hotelName?: string | null;
  hotelAddress?: string | null;
  roomCount?: number | null;
  nightlyRate?: number | null;
  stayTaxesAndFees?: number | null;
};

/**
 * SERV-2626: the identity fields mirror `TravelerIdentity` (shared/flightBooking.ts), which
 * carries the dispatcher's request-flow view of the traveler. Duplicated here rather than
 * imported so the API-boundary types stay independent of that feature module.
 */
export type FlightBookingPassenger = {
  technicianName?: string;
  legalGivenName?: string;
  legalFamilyName?: string;
  bornOn?: string;
  gender?: string;
  /** SERV-3077: "mr" | "ms" | "mrs". */
  title?: string;
  email?: string;
  phone?: string;
  documentNumber?: string;
};

export type FlightBookingAttributes = {
  bookingType?: string;
  departureLocation?: string;
  destination?: string;
  flightDate?: string;
  preferredTime?: string;
  preferredAirline?: string;
  passengers: FlightBookingPassenger[];
  /**
   * SERV-3166: the fare breakdown the officer records with the booking. The all-in total lives on
   * the item's own `anticipatedCost` column; these two are the parts of it, flat because the
   * backend merges attributes one level deep. Absent on bookings recorded before SERV-3166.
   */
  fareBase?: number | null;
  fareTaxesAndFees?: number | null;
  /**
   * SERV-3166: when the fare was recorded on the booking. Dispatch's estimate shares the
   * `anticipatedCost` column, so this is what tells a fare from a guess (travelPricing.ts).
   */
  fareRecordedAt?: string;
};

export type TransportationRentalAttributes = {
  rentalCompany?: string;
  vehicleType?: string;
  pickupLocation?: string;
  dropoffLocation?: string;
  travelDate?: string;
  pickupTime?: string;
  dropoffTime?: string;
  technicians: string[];
  drivers: string[];
  /** SERV-2868 AC4/AC5 - see the identical fields on `EquipmentAttributes` above. */
  reservationBranch?: string;
  reservationAccessories?: string[];
  reservationDriverVerified?: boolean | null;
  /** SERV-2868 AC6 - see the identical fields on `EquipmentAttributes` above. */
  offRentAt?: string;
  offRentCallLog?: string;

  /**
   * SERV-2976. The vehicle booking's own terms, the tail of the hire, and the officer's two gates.
   *
   * These live on `attributes` rather than on columns of their own, following SERV-2868, which put
   * `reservationBranch`/`offRentAt`/`rentalEndAt` here for the same reason. `attributes` is a real
   * persisted `Json?` column; the backend accepts it as an open record
   * (procurement/validation.ts:303) and merges it ONE LEVEL DEEP via `mergeAttributes`
   * (procurement/service.ts:288), so a partial write cannot wipe sibling keys. That one-level merge
   * is why every field below is flat: a nested `booking: {...}` object would be replaced wholesale
   * by the next write that touched any part of it.
   *
   * Every field is optional. Rows written before this ticket carry none of them and must read as
   * "not captured" rather than as zero or as a missing booking.
   */

  /**
   * The return date the lane never had. `travelDate` above is the COLLECTION date; the wizard
   * appends "Collect ... Return ..." to the free-text note (createRequestPayload.ts:541) and
   * nothing could read it back, so `rentalFor` hard-coded `end: ""` for this item type
   * (procurementRequestAdapter.ts:356). Named `rentalEndAt` to match `EquipmentAttributes`, so the
   * adapter reads one field name across both rental shapes.
   */
  rentalEndAt?: string;

  /**
   * SERV-2976. What dispatch knows that the shape had nowhere to put.
   *
   * All four were being appended to the request's free-text note by the create wizard, or not
   * captured at all, which meant the Request tab could show an officer a note to read rather than a
   * field to check, and nothing downstream could act on any of them. `transmission` in particular
   * had a picker in the wizard already (`transmissions` in procurementOptions.ts) whose answer went
   * straight into prose.
   *
   * `jobEndsAt` is load-bearing beyond display: a job that ends after the vehicle is due back is a
   * booking that will run late, and that is checkable only once both dates are fields.
   */
  transmission?: string;
  jobEndsAt?: string;
  /**
   * How many people are travelling, including the driver.
   *
   * A number rather than a reading of `technicians` below, because `technicians` is written as a
   * hard-coded empty array by the create wizard and always has been - this lane knows the driver
   * and nothing about who else is in the vehicle. The headcount is the part that decides whether
   * the class dispatch asked for is big enough, and it is what dispatch can actually supply.
   */
  crewSize?: number;
  costCentre?: string;
  budgetCode?: string;

  /**
   * SERV-2976. The branch's opening hours, as the counter gave them on the call.
   *
   * Captured at reservation rather than held against the supplier, because no column exists for it:
   * `ProcurementSupplierLocations` stores a label and an address and nothing about when the place is
   * open. That is where this belongs in the end - opening hours are a fact about a branch, not about
   * one hire - and moving it there is a migration on a table every lane reads, so it is its own
   * ticket. Recorded here it is at least true of this booking, and it makes "will the branch be open
   * when the driver gets there" answerable instead of permanently unknown.
   */
  branchHours?: string;

  /** The officer's first gate: the dispatcher's details confirmed, before anything else unlocks. */
  detailsConfirmedAt?: string;
  detailsConfirmedBy?: string;

  /**
   * The second gate. Recorded as an event rather than a boolean: WHO cleared a driver and WHEN is
   * the whole evidentiary value of a gate that has no override, and a bare `true` cannot answer
   * either. `driverClearedName` is the name the booking must be held in.
   */
  driverClearedAt?: string;
  driverClearedBy?: string;
  driverClearedName?: string;
  /** The provider the clearance was made against, so a later visit reassignment is detectable. */
  driverClearedProviderId?: string;

  /** The booking itself. `confirmationNumber` is what the counter asks for at the desk. */
  confirmationNumber?: string;
  bookingName?: string;
  bookingCompany?: string;
  bookingBranch?: string;
  bookingPaymentMethod?: string;
  bookingCollectAt?: string;
  bookingDueBackAt?: string;
  bookingDays?: number;
  /**
   * When the reservation was made and by whom. Distinct from `bookingCollectAt`, which is when the
   * vehicle is due to be handed over: the Activity feed is built from these stamps, and without one
   * of its own the reservation is the only step in the lane that cannot be placed in time.
   */
  bookingReservedAt?: string;
  bookingReservedBy?: string;

  /**
   * The rate breakdown. Held as separate numbers rather than one computed total because the point
   * of the Cost tab is that the headline daily rate is not what lands on the card, and a single
   * stored figure cannot show the gap. Money as `number` here, not `string`: these are attributes
   * JSON, not the Decimal columns (`anticipatedCost`, `paymentAmount`) the API returns as strings.
   */
  rateDailyAmount?: number;
  /**
   * SERV-3076: the single figure the officer records at Reserve now that the rate breakdown is
   * hidden. Absent on bookings made before it, which carry the `rate*` terms above instead.
   */
  bookingTotalCost?: number;
  rateMilesPerDay?: number;
  ratePerMileOver?: number;
  rateEstimatedMiles?: number;
  rateFuelPolicy?: string;
  ratePrepaidFuelPerGallon?: number;
  rateCollisionWaiverPerDay?: number;
  rateInsuranceExcess?: number;
  rateYoungDriverPerDay?: number;
  rateAdditionalDriverPerDay?: number;
  rateAdditionalDrivers?: number;
  rateOneWayFee?: number;
  rateTaxPercent?: number;

  /** Collection: the odometer reading is the only evidence that settles a mileage dispute later. */
  collectedAt?: string;
  collectedBy?: string;
  collectedPlate?: string;
  collectedVehicleGiven?: string;
  collectedOdometer?: number;
  collectedFuelLevel?: string;

  /**
   * The return. `COMPLETED` cannot carry any of this (ITEM_STATUS_ALLOWED_FROM, service.ts:246) and
   * the `return*` COLUMNS model sending a purchase back to a vendor with an RMA, not handing a car
   * back to a branch, so none of them fit.
   */
  returnedAt?: string;
  /**
   * The officer who settled it, not the person who handed the keys back. Settling is a procurement
   * act - it prices the damage and the fuel - and the Activity feed names whoever did it.
   */
  returnedBy?: string;
  returnedOdometer?: number;
  returnedFuelLevel?: string;
  returnedFuelCharge?: number;
  returnedDamageLogged?: boolean;
  returnedDamageNote?: string;
  returnedDamageCharge?: number;
  returnedLateHours?: number;
  /**
   * What the hire actually cost once settled, against `anticipatedCost`'s quoted all-in.
   *
   * Here rather than on the item's `paymentAmount` column because `UpdateItemSchema`
   * (procurement/validation.ts:267) does not accept `paymentAmount` - a payload carrying it is
   * stripped by Zod and answers 200 having written nothing. `paymentAmount` is set only by
   * `logItemPayment`, which requires a card, and settling a return is not paying for one.
   */
  settledFinalCost?: number;
};

export const PRICING_REQUEST_ITEM_SOURCES = ["manual", "document", "link"] as const;
export type PricingRequestItemSource = (typeof PRICING_REQUEST_ITEM_SOURCES)[number];

export const PRICING_REQUEST_INTENTS = [
  "sooner-eta",
  "cheaper-alternative",
  "local-sourcing",
] as const;
export type PricingRequestIntent = (typeof PRICING_REQUEST_INTENTS)[number];

export type PricingAttributes = ItemDimensionAttributes & {
  make?: string;
  model?: string;
  neededBy?: string;
  description?: string;
  attachmentS3Keys: string[];
  serialNumber?: string;
  unit?: string;
  source?: PricingRequestItemSource;
  /**
   * SERV-2658 AC7: the specification the dispatcher confirmed from the link, keyed by option-set
   * key. Same key the online-purchase lane writes, so a later stage need not know which lane
   * produced the item. Declared because a spread skips excess-property checks: this compiled
   * without it and would have been an undocumented field nobody could find from the type.
   */
  selectedSpecs?: Record<string, string>;
  /**
   * SERV-2658: the dispatcher picks one intent per request, not per item, but the payload has no
   * per-request attributes bag - every item in the request carries the same intent/ETA/address so
   * a reader never has to reconcile conflicting copies across a request's own items.
   */
  intent?: PricingRequestIntent;
  currentEta?: string;
  /**
   * Captured from the work order at submit time, not resolved later from the work order's current
   * address - a work order address that changes after submission must not silently move the
   * search reference.
   */
  localSourcingAddress?: string;
};

/**
 * SERV-2665: one person the wizard said needs access, as persisted on the access request item.
 * Contact fields are nullable: a provider snapshot is only as complete as the work-order technician
 * it was taken from, and the attribute bag is untyped JSON on the way back in.
 */
export type StorageAccessRecipient =
  | {
      kind: "provider";
      serviceProviderId: string;
      fullName?: string | null;
      phone?: string | null;
      email?: string | null;
    }
  | {
      kind: "individual";
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
      phone?: string | null;
      licenseS3Key?: string;
    };

export type StorageUnitAttributes = {
  storageSize?: string;
  access?: string;
  amenities?: string[];
  /**
   * SERV-2974: what the unit is for - materials, equipment, units, parts, other.
   *
   * The wizard has collected this since SERV-2626 and `createRequestPayload` never read it, so it
   * was typed on every storage request and thrown away at the boundary. It rides in the attribute
   * bag beside the other requested terms rather than as a column, because it is the DISPATCHER's
   * statement of intent - the same kind of fact as the size and the amenities - not an outcome the
   * officer records.
   */
  storagePurposes?: string[];
  entryDate?: string;
  exitDate?: string;
  /*
   * SERV-2665: which of the two storage requests this is. Absent on every row written before that
   * ticket, and absent on a rental, so readers must treat "no kind" as renting a new unit rather
   * than as a missing value.
   */
  storageKind?: "rent-new" | "request-access";
  /** The unit access is being requested to. Only on a request-access row. */
  storageUnitId?: string;
  accessChannel?: "SMS" | "EMAIL";
  /** Untyped JSON on read; narrow with `parseStorageAccessRecipients`. */
  storageAccessRecipients?: unknown;
};

/**
 * SERV-2667: the Online Purchase lane's link-derived items have no dedicated attributes shape of
 * their own yet. `selectedSpecs` carries the dispatcher's picks from the link's option sets
 * (colour, size, ...) as data rather than folding them into a rendered string, so SERV-2672 can
 * read individual values later. `currency` is recorded rather than assumed because a supplier's
 * page can price in a currency other than the ones already priced in USD elsewhere in this
 * module - there is no dedicated money-with-currency column yet, so it travels alongside the
 * item here until one exists.
 */
export type PurchaseLinkItemAttributes = {
  /** Chosen from an option set the product page exposed, keyed by that set's key. */
  selectedSpecs?: Record<string, string>;
  /**
   * Typed by the dispatcher when the page exposed no option sets, which covers a link that could
   * not be read. Separate from `selectedSpecs` so a reader can tell a chosen value from a freehand
   * one rather than having to guess.
   */
  manualSpec?: string;
  /**
   * SERV-2658: the manufacturer and its own model or part number, read from the product page or
   * typed. Written under the same keys the pricing lane uses, so a reader needs one lookup rather
   * than one per lane.
   *
   * Not yet displayed for this lane, and review is why that is stated here rather than assumed: the
   * item drawer renders make and model only for its PRICING and rental branches, and an
   * online-purchase item is PARTS, MATERIALS, CONSUMABLES or UNIT, which fall to the
   * delivery/pickup branch showing name, URL, quantity and cost. So these are captured and stored
   * correctly and an officer will not see them in the drawer until that branch is taught to. Same
   * gap the pre-existing selectedSpecs and manualSpec have in this lane; it is a drawer ticket, not
   * a reason to hold the capture back.
   */
  make?: string;
  model?: string;
  currency?: string;
};

/**
 * SERV-3360: the per-item purchase record on the online-purchase and outbound phone-sale lanes.
 * Flat keys beside whatever the lane already stores, because the server merges attributes one level
 * deep. `purchaseTax` is not `tax` and `ppTotal` is not `total`: those two feed the outbound
 * quotation total and its NTE check. `ppTotal` is recomputed by the server on every write.
 */
export type PurchaseRecordAttributes = {
  serialNumber?: string | null;
  invoicedCost?: number | null;
  publicInternetCost?: number | null;
  confirmedQty?: number | null;
  purchasePrice?: number | null;
  purchaseTax?: number | null;
  ppTotal?: number | null;
  cardLast4?: string | null;
  approxShipDate?: string | null;
  approxShipDateText?: string | null;
  deliveredAt?: string | null;
  trackingUrl?: string | null;
  infoProvided?: boolean | null;
  infoProvidedComment?: string | null;
};

/*
 * SERV-3360: a Linear Feet line's pieces and length per piece, or a Square Feet line's length and
 * width. Flat beside `unit` because the PATCH merge is one level deep; null clears a stale size.
 */
export type ItemDimensionAttributes = {
  pieces?: number | null;
  lengthFt?: number | null;
  widthFt?: number | null;
};

export type ItemUnitAttributes = ItemDimensionAttributes & {
  unit?: string;
  /** SERV-2867: the item's specification, under the key the create wizard writes. */
  description?: string;
  /** SERV-2867: a supplier picked on Edit Item, stored by name rather than as a record; null clears it. */
  supplier?: string | null;
};

/** SERV-3360: one person picked in the create wizard's Team Leader(s), stored on every item. */
export interface ProcurementTeamLeader {
  userId: string;
  name: string;
}

export type ProcurementItemAttributes =
  | ItemUnitAttributes
  | PhoneSaleAttributes
  | EquipmentAttributes
  | HotelBookingAttributes
  | FlightBookingAttributes
  | TransportationRentalAttributes
  | PricingAttributes
  | StorageUnitAttributes
  | PurchaseLinkItemAttributes
  | PurchaseRecordAttributes;

export type ProcurementReceiptPart = {
  partName: string;
  make?: string;
  modelNo?: string;
  quantity?: number;
  cost?: number;
};

export type ProcurementTrackingNumber = {
  number: string;
  arrivalDate?: string;
  arrivalTime?: string;
  carrier?: string;
  /** SERV-3440: http(s) only, checked server side. */
  url?: string;
};

// ── List / detail entities (money values are Decimal-serialized strings) ────

export type ProcurementSupplierLocation = {
  id: string;
  label: string;
  address: string | null;
  address1?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  /**
   * SERV-3014: the vendor's separate returns depot, where returns actually go when that is not
   * where the item was bought. Optional AND nullable: most locations have none, and every
   * location predating this change has none - both mean "return it where you bought it", which
   * is the fallback the resolver applies. Do not tighten these from the Prisma schema.
   */
  returnsAddress1?: string | null;
  returnsAddress2?: string | null;
  returnsCity?: string | null;
  returnsState?: string | null;
  returnsZipcode?: string | null;
};

/**
 * SERV-2861: a row in the Open locations popup.
 *
 * Every optional field is nullable because the backend column is nullable - do not tighten these
 * from the Prisma schema. There is no store-number field: where a branch number is known it is
 * inside the free-text `label`.
 */
export type ProcurementSupplierLocationListItem = {
  id: string;
  label: string;
  address: string | null;
  address1: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
  phoneNumber: string | null;
  /** Whether THIS tenant has actually ordered at this branch. Derived server-side from items. */
  used: boolean;
};

export type ProcurementSupplierLocationListResult = {
  data: ProcurementSupplierLocationListItem[];
  pagination: APPagination;
};

export const SUPPLIER_TIERS = ["PREFERRED", "STANDARD"] as const;
export type ProcurementSupplierTier = (typeof SUPPLIER_TIERS)[number];

export const VENDOR_ACCOUNT_PAYMENT_TERMS = [
  "COD",
  "NET_10",
  "NET_13",
  "NET_15",
  "NET_30",
  "NET_45",
  "NET_60",
  "REVOLVING",
  "CARD_ONLY",
] as const;
export type VendorAccountPaymentTerms = (typeof VENDOR_ACCOUNT_PAYMENT_TERMS)[number];

const KNOWN_PAYMENT_TERMS_LABELS: Record<VendorAccountPaymentTerms, string> = {
  COD: "COD (close of day)",
  NET_10: "Net 10",
  NET_13: "Net 13",
  NET_15: "Net 15",
  NET_30: "Net 30",
  NET_45: "Net 45",
  NET_60: "Net 60",
  REVOLVING: "Revolving",
  CARD_ONLY: "Card only",
};

/**
 * Supply houses negotiate their own terms, so the stored value is free text. Known
 * terms get a friendly label; anything else is shown exactly as the officer typed it.
 */
export const paymentTermsLabel = (terms: string | null | undefined): string => {
  if (!terms) return "—";
  return KNOWN_PAYMENT_TERMS_LABELS[terms as VendorAccountPaymentTerms] ?? terms;
};

export const VENDOR_ACCOUNT_STATUSES = ["ACTIVE", "ON_HOLD", "CLOSED"] as const;
export type VendorAccountStatus = (typeof VENDOR_ACCOUNT_STATUSES)[number];

export const VENDOR_ACCOUNT_STATUS_LABELS: Record<VendorAccountStatus, string> = {
  ACTIVE: "Active",
  ON_HOLD: "On Hold",
  CLOSED: "Closed",
};

export const VENDOR_ACCOUNT_LOCATION_SCOPES = ["NATIONAL", "SPECIFIC_LOCATIONS"] as const;
export type VendorAccountLocationScope = (typeof VENDOR_ACCOUNT_LOCATION_SCOPES)[number];

export const VENDOR_ACCOUNT_LOCATION_SCOPE_LABELS: Record<VendorAccountLocationScope, string> = {
  NATIONAL: "National",
  SPECIFIC_LOCATIONS: "Specific locations",
};

export const VENDOR_ACCOUNT_PROGRAMS = [
  "Pro Xtra",
  "MyLowe's Pro Rewards",
  "National Account",
  "PRO Plus",
  "Member",
  "Standard",
] as const;

/** One payment card linked to a vendor account (service.ts:825-836). The link row's own id is
 * distinct from the card's id, because the same card can be linked to several vendor accounts. */
export type VendorAccountCardLink = {
  id: string;
  paymentCardId: string;
  label: string | null;
  isDefault: boolean;
  card: {
    id: string;
    nameOnCard: string;
    brand: string;
    last4: string;
  };
};

export type ProcurementVendorAccountSupplier = {
  id: string;
  name: string;
  tier: ProcurementSupplierTier | null;
};

/**
 * The API's vendor-account shape (repository.ts:276-287, service.ts:797-838), not the supplier's.
 * `id`/`tenantId`/`supplierId` make every mutation routable; `cards` is what the API calls its own
 * linked-card collection (the API never returns a field named `linkedCards`).
 */
export type ProcurementVendorAccount = {
  id: string;
  tenantId: string;
  supplierId: string;
  supplier: ProcurementVendorAccountSupplier;
  accountName: string;
  program: string | null;
  accountNumber: string;
  paymentTerms: string;
  creditLimit: string | null;
  repFirstName: string | null;
  repLastName: string | null;
  repName: string | null;
  repPhone: string | null;
  repEmail: string | null;
  portalUsername: string | null;
  /** READ field: never a readable password. */
  portalPasswordSet: boolean;
  locationScope: VendorAccountLocationScope | null;
  locationNotes: string | null;
  notes: string | null;
  status: VendorAccountStatus;
  cards: VendorAccountCardLink[];
  createdAt: string;
  updatedAt: string | null;
  /**
   * Distinct supplier locations this tenant has actually ordered from, counted server-side.
   * Per supplier, not per vendor account - two accounts at one supplier report the same number.
   * Nullable: a caller that did not ask for the aggregate gets no number, not a misleading zero.
   */
  usedLocationsCount: number | null;
};

export type CreateProcurementVendorAccountInput = {
  supplierId: string;
  accountName: string;
  accountNumber: string;
  paymentTerms: string;
  program?: string | null;
  creditLimit?: number | null;
  repFirstName?: string | null;
  repLastName?: string | null;
  repName?: string | null;
  repPhone?: string | null;
  repEmail?: string | null;
  portalUsername?: string | null;
  /** Write-only. Encrypted at rest by the API and never returned on read. */
  portalPassword?: string;
  locationScope?: VendorAccountLocationScope | null;
  locationNotes?: string | null;
  notes?: string | null;
  status?: VendorAccountStatus;
};

export type UpdateProcurementVendorAccountInput = Partial<
  Omit<CreateProcurementVendorAccountInput, "supplierId">
>;

export type LinkProcurementVendorAccountCardPayload = {
  paymentCardId: string;
  isDefault: boolean;
};

export type UnlinkProcurementVendorAccountCardPayload = {
  paymentCardId: string;
};

/**
 * SERV-2966: a vendor's return terms, from the supplier record that is now the only place they
 * live. Three other copies used to exist: two hardcoded tables in this app's own source and a
 * lookup that persisted nothing.
 *
 * Every field is nullable because the record genuinely may not know. Callers must not coalesce an
 * unknown into a convenient default: "no restocking fee" and "we have not checked their restocking
 * fee" lead to different decisions, and defaulting the second to the first is how a return gets
 * refused at the dock.
 */
/**
 * SERV-2966 gap 4: whether the money actually arrived, kept separate from whether a vendor said it
 * would. `expected` is what the vendor claimed; `confirmed` is the sum of what was seen to land.
 *
 * AWAITING_CREDIT is the state that did not exist before: a settled return the vendor never paid
 * used to be indistinguishable from one they did.
 */
/** SERV-2966 gap 6: the payload that writes a vendor's terms onto the supplier record. */
export type ProcurementSupplierReturnPolicyPayload = {
  windowDays: number | null;
  restockingPct: number | null;
  rmaRequired: boolean | null;
  originatingBranchOnly: boolean | null;
  returnsDeskHours: string | null;
  refundsToOriginalCardOnly: boolean | null;
  returnFreightPaidBy: "vendor" | "buyer" | null;
  nonReturnable: string[];
  policyUrl: string | null;
};

/** SERV-2966 gap 4: one credit confirmed as actually received against a settled return. */
export type RecordReturnCreditPayload = {
  amount: number;
  confirmedAt: string;
  method: ProcurementReturnCreditMethod;
  reference?: string | null;
  notes?: string | null;
};

export type ProcurementReconciliationState =
  | "AWAITING_CREDIT"
  | "RECONCILED"
  | "SHORT"
  | "OVER"
  | "NOT_APPLICABLE";

export type ProcurementReturnCreditMethod =
  | "CARD_REFUND"
  | "ACCOUNT_CREDIT"
  | "CHECK"
  | "BANK_TRANSFER"
  | "CREDIT_MEMO";

export type ProcurementReturnCredit = {
  id: string;
  amount: string;
  confirmedAt: string;
  method: ProcurementReturnCreditMethod;
  reference: string | null;
  notes: string | null;
  createdAt: string;
};

export type ProcurementReturnReconciliation = {
  state: ProcurementReconciliationState;
  /** Null when nobody recorded what the vendor claimed; not the same as zero. */
  expected: string | null;
  confirmed: string;
  /** Negative is a shortfall. Null when there is nothing to compare against. */
  variance: string | null;
  credits: ProcurementReturnCredit[];
};

export type ProcurementSupplierReturnPolicy = {
  windowDays: number | null;
  restockingPct: number | null;
  rmaRequired: boolean | null;
  originatingBranchOnly: boolean | null;
  returnsDeskHours: string | null;
  refundsToOriginalCardOnly: boolean | null;
  returnFreightPaidBy: "vendor" | "buyer" | null;
  nonReturnable: string[];
  policyUrl: string | null;
  /** Null means never confirmed, which reads as unknown rather than as safe. */
  verifiedOn: string | null;
};

export type ProcurementSupplier = {
  id: string;
  name: string;
  websiteUrl: string | null;
  phoneNumber: string | null;
  tier?: ProcurementSupplierTier;
  accountNumber?: string | null;
  locations: ProcurementSupplierLocation[];
  /** SERV-2966: optional because older payloads predate it; absent reads as terms unknown. */
  returnPolicy?: ProcurementSupplierReturnPolicy | null;
};

export type ProcurementPaymentCard = {
  id: string;
  nameOnCard: string;
  brand: CardBrand;
  last4: string;
  /* SERV-2638: the supplier the card is held against. Null on every tenant-wide card. */
  supplierId?: string | null;
};

/** SERV-3500: one file on a conversation message. `url` is null when the server could not sign it. */
export type ProcurementConversationAttachment = {
  fileName: string;
  contentType: string;
  size: number;
  url: string | null;
};

/** SERV-3500: the shape the four send endpoints take for an uploaded file. */
export type ConversationAttachmentPayload = {
  s3Key: string;
  fileName: string;
  contentType: string;
  size: number;
};

export type ProcurementRequestActivityVisibility = "PUBLIC" | "INTERNAL";

/**
 * SERV-2508 (adopted by SERV-2972 for the online-purchase lane's Activity tab): one event in a
 * request's history.
 *
 * `kind` is a plain string, not a union, because the backend column is a varchar that grows per
 * lane - narrowing it here would make an event added server-side fail to parse on a frontend that
 * had not been redeployed, for a feed that only reads `description`.
 */
export type ProcurementRequestActivity = {
  id: string;
  requestId: string;
  kind: string;
  /** SERV-3353: optional and nullable so a payload from before the column existed reads PUBLIC. */
  visibility?: ProcurementRequestActivityVisibility | null;
  description: string;
  subjectItemId: string | null;
  metadata: Record<string, unknown> | null;
  /** SERV-3500: absent on a row from before attachments existed. */
  attachments?: ProcurementConversationAttachment[] | null;
  createdAt: string;
  actedByUser: { id: string; firstName: string | null; lastName: string | null } | null;
};

/** SERV-3353 3.D: one row of GET /procurement/request/:id/mentionable. */
export type ProcurementMentionableUser = {
  id: string;
  label: string | null;
  /** Tells apart two teammates with the same name. Optional for a payload from before it existed. */
  roleLabel?: string | null;
  /** SERV-3353 3.I: the request's creator. She cannot see internal notes. */
  isRequester?: boolean | null;
};

/**
 * SERV-2973: the return clock on a queue row. Null when nothing on the request is out on hire,
 * which is every request on every lane but equipment rental.
 */
export type ProcurementReturnClock = {
  daysLeft: number;
  overdue: boolean;
  dueSoon: boolean;
  accrued: string | null;
  liveCount: number;
};

export type ProcurementRequestListItem = {
  /** SERV-2973: optional as well as nullable, so a cached pre-2973 response reads as "no clock". */
  returnClock?: ProcurementReturnClock | null;
  /** SERV-2670. The dash suffix; null on a parent and on any request never split. */
  childSequence: number | null;
  /** SERV-2670. Set only on a child. */
  parentRequestId: string | null;
  /** SERV-2670. How many children this row has; 0 unless it is a parent. */
  childCount: number;
  id: string;
  requestNumber: number;
  entityName: string;
  entityAbbreviation: string | null;
  /** SERV-3232: the record's entity; nullable at the boundary. */
  tenantId?: string | null;
  workOrderId: string;
  workOrderNumber: string;
  category: ProcurementCategory;
  status: ProcurementRequestStatus;
  approvalStatus: ProcurementApprovalStatus | null;
  derivedStatus: ProcurementDerivedStatus;
  itemCount: number;
  totalAnticipatedCost: string | null;
  /**
   * SERV-2882: backs the queue's Quotation column - a download link per key, or a dash. Optional at
   * the boundary because an API deployed before this column existed sends nothing; read it through
   * `quotationKeysOf` rather than indexing it.
   */
  quotationS3Keys?: string[] | null;
  anticipatedCost: string | null;
  createdAt: string;
  updatedAt: string;
  requestorRole: string | null;
  /** Officer the request is assigned to. Null until someone claims or is assigned it. */
  procurementBy: string | null;
  procurementByName: string | null;
  /** Nullable: not every request resolves to a client on legacy or malformed work orders. */
  clientName: string | null;
  clientPriority: string | null;
  itemsTotal: number;
  itemsFulfilled: number;
  expFulfillmentAt: string | null;
  hasTracking: boolean;
  /**
   * SERV-2632. A supplier is set per item, so a request that was never split can span several.
   * The name arrives only when the items agree on one; otherwise the count is what the queue
   * renders, so a row never claims a single supplier it does not actually have.
   */
  supplierName: string | null;
  supplierCount: number;
  /** SERV-2632. The branch label, e.g. "Ashburn #4647". Null unless the items share one. */
  supplierLocationLabel: string | null;
  supplierLocationCount: number;
  /**
   * SERV-2632. The lane the request was raised through, and what the Procurement Type column
   * shows. Not the same as `category`: five lanes all store PURCHASE_ITEMS, so the category could
   * not tell a phone sale from a storage unit. Derived by the server so the label and the filter
   * come from one rule.
   */
  lane: ProcurementLane;
  /**
   * SERV-2975: the pricing request this row was raised into, so a Request for Pricing opens on the
   * officer's pricing screen instead of the generic ticket. Null on every non-PRICING row, and on
   * a PRICING row raised before the two were linked.
   */
  pricingRequestId: string | null;
  /**
   * SERV-2859 AC1.5/ADDENDUM A5. Derived read-only, beside `hasTracking`, from the request's
   * non-deleted items - nothing is stored. `null` when no item has an open or failed return
   * (AC1.6), or when the only return is REFUNDED (AC1.7, closed, shown under neither state).
   * Optional rather than required: a field newly added to an existing response, so a frontend
   * running ahead of a backend that predates this ticket receives none of the three at all.
   */
  returnState?: ProcurementReturnState | null;
  /** D4: the least-advanced live stage, or the shared failure stage, among the shown category. */
  returnStage?: ReturnStatus | null;
  /** D4: how many items are in the shown category (live, or failed). */
  returnItemCount?: number;
};

export type ProcurementRequestCountsResult = {
  total: number;
  /** Partial: a key added on the FE before the API deploys it is absent from the payload. */
  byDerivedStatus: Partial<Record<ProcurementDerivedStatus, number>>;
  /** Requests `procurementBy` on the calling officer. */
  mine: number;
};

/**
 * SERV-2887: one carrier scan of the return shipment, as the API serves it.
 *
 * Every field is nullable because this is the carrier's data relayed through EasyPost: a scan with
 * no recorded location, or one whose timestamp the carrier sent unparseable, is still a real
 * movement the drawer should show.
 */
export type ReturnTrackerEvent = {
  status: string | null;
  message: string | null;
  /** ISO 8601, the CARRIER's scan time, not the time we received the webhook. */
  occurredAt: string | null;
  location: string | null;
};

export type ProcurementItemListRow = {
  id: string;
  requestId: string;
  requestNumber: number;
  entityName: string;
  entityAbbreviation?: string | null;
  workOrderId: string;
  workOrderNumber: string;
  itemType: ProcurementItemType;
  name: string | null;
  quantity: number | null;
  supplierId: string | null;
  supplierName: string | null;
  supplierTier?: ProcurementSupplierTier | null;
  supplierLocationLabel: string | null;
  /**
   * SERV-2959. The estimate recorded on the PARENT REQUEST, not on this item - one figure for the
   * whole request, which SERV-2854 deliberately moved off the item. It repeats on every row of the
   * request, so summing it per row multiplies it by the item count. The Est. Cost column falls back
   * to it for display; the money tiles add it once per request. `anticipatedCost` remains this
   * item's own figure and is the one that may safely be summed.
   */
  requestAnticipatedCost: string | null;
  /**
   * SERV-2650. The PARENT REQUEST's "Not to exceed", the high end of the cost range whose low end is
   * `requestAnticipatedCost`. Repeats on every row of the request, so it is shown, never summed.
   * Optional because a list response from a backend deployed before this field returns undefined.
   */
  requestNte?: string | null;
  /**
   * SERV-3168. What has been paid against the PARENT REQUEST - the phone-sale lanes' single run
   * payment, which never marks a line PAID. Repeats on every row of the request, so the money tiles
   * add it once per request. Null when nothing is recorded at the request level, and optional
   * because a backend deployed ahead of this field omits it.
   */
  requestPaidTotal?: string | null;
  fulfillmentMethod: FulfillmentMethod;
  expFulfillmentAt: string | null;
  status: ProcurementItemStatus;
  paymentStatus: ProcurementPaymentStatus;
  returnStatus: ReturnStatus | null;
  returnMethod: ReturnMethod | null;
  returnReason?: string | null;
  rmaNumber?: string | null;
  requestorRole?: string | null;
  requestorName?: string | null;
  approvalStatus?: ProcurementApprovalStatus | null;
  anticipatedCost: string | null;
  paymentAmount?: string | null;
  attributes: ProcurementItemAttributes | null;
  fulfillmentInfo: ProcurementFulfillmentInfo | null;
  /**
   * SERV-2977: the travel booking's own record.
   *
   * Optional AND nullable at the boundary, per the repo's standing rule for a field newly
   * surfaced on an existing table: legacy rows and older list projections can return neither, and
   * a non-null Prisma column does not promise the API sent a value.
   *
   * `refundable` is three-valued and must stay so. Coalescing an unknown to `false` raises a false
   * alarm on the at-risk total; coalescing it to `true` hides a real one.
   *
   * `recordLocator` and `eTicketNumber` are AIRLINE records and are always null on a hotel
   * booking - a hotel reservation has neither.
   */
  tripLegOrder?: number | null;
  confirmationNumber?: string | null;
  recordLocator?: string | null;
  eTicketNumber?: string | null;
  heldUntil?: string | null;
  refundable?: boolean | null;
  changeFee?: string | null;
  freeCancellationUntil?: string | null;
  /**
   * SERV-2859 AC4.6: `toItemListRow` carries these so the returns screen renders carrier, tracking,
   * label-purchased date, return due-by and QR availability without a per-row `GET /item/:id`.
   * Nullable at the boundary - a field newly surfaced on an existing table, not backfilled.
   */
  returnCarrier?: string | null;
  returnTrackingNumber?: string | null;
  returnLabelUrl?: string | null;
  returnLabelQrUrl?: string | null;
  returnLabelCost?: string | null;
  returnLabelPurchasedAt?: string | null;
  returnEasypostTrackerId?: string | null;
  /**
   * SERV-2887: the carrier's own view of the return leg. Optional AND nullable at the boundary:
   * a row whose label predates this change carries a tracker id and nothing else (no backfill was
   * done), so these must render as plain text rather than as a link that goes nowhere.
   * returnTrackerEvents is served as [] rather than null, so "no scans yet" and "nothing stored"
   * read the same to the UI.
   */
  returnTrackerPublicUrl?: string | null;
  returnTrackerStatus?: string | null;
  returnTrackerLastEventAt?: string | null;
  /** SERV-3014: what the delivered-but-uncredited warning reads on the Returns screen. */
  returnDeliveredAt?: string | null;
  /*
   * Not carried on the list row: only the drawer renders the scan history, and it reads the detail
   * endpoint. The API leaves it off list responses rather than shipping 50 scans per returned item.
   */
  returnDueBy?: string | null;
  supplierLocationId?: string | null;
  /** SERV-2859 AC6/ADDENDUM A1-A2: the QR send-state, nullable - null means "predates this change". */
  returnQrEmailStatus?: ReturnQrEmailStatus | null;
  returnQrEmailSentAt?: string | null;
  returnQrEmailTo?: string | null;
  returnQrEmailFailureReason?: string | null;
  /**
   * SERV-2885: how the return ended, what the vendor actually credited, and when it settled - the
   * three columns a settled return is kept on the returns screen FOR. Carried on the list row for
   * the same reason AC4.6 carried carrier/tracking/due-by: otherwise the screen needs a per-row
   * `GET /item/:id` to render its own table.
   *
   * Optional and nullable at the boundary, and not only out of habit: these are newly surfaced on
   * an existing table, so every row that settled before this shipped returns null, and a list
   * projection deployed ahead of the mapper returns undefined. A missing credit is not 0.
   */
  returnClosureOutcome?: string | null;
  returnCreditAmount?: string | null;
  returnedAt?: string | null;
  /**
   * SERV-2885 AC C3: whose mistake it was, which decides who pays the return freight. On the list
   * row so the returns screen can warn before an officer generates a label at our cost on a
   * vendor's error - the warning belongs on the screen where the label is actually bought.
   */
  returnFault?: string | null;
};

export type ProcurementRequestItem = {
  /**
   * SERV-2973: the booked hire behind a rental line. Empty on every non-rental item, so this is
   * safe to read on any lane. At most one live agreement per line; a list because that is the
   * relation's shape.
   */
  rentalAgreements?: ProcurementRentalAgreement[] | null;
  /**
   * SERV-2671 FR1: shipping is entered and read per item, not once per order. Nullable at the
   * boundary because most items have none recorded, and a missing fee is not zero.
   */
  shippingCost: string | null;
  shippingCurrency: string | null;
  id: string;
  requestId: string;
  itemType: ProcurementItemType;
  status: ProcurementItemStatus;
  name: string | null;
  url: string | null;
  quantity: number | null;
  anticipatedCost: string | null;
  fulfillmentMethod: FulfillmentMethod;
  supplierId: string | null;
  supplierLocationId: string | null;
  supplier:
    | (Pick<
        ProcurementSupplier,
        "name" | "websiteUrl" | "phoneNumber" | "tier" | "accountNumber"
      > & {
        /*
         * SERV-3014: who pays return freight as a standing term of this vendor's policy, which
         * is NOT the same fact as returnFault (whose mistake one return was). Optional AND
         * nullable: null means the policy was never confirmed, which must read as unknown rather
         * than as safe, and a payload predating this carries nothing at all.
         */
        returnFreightPaidBy?: ReturnPolicyFreightPayer | null;
        /**
         * SERV-3014: when this vendor's return terms were last confirmed. Null means never,
         * which the stage rail shows as an unticked step rather than assuming they were checked.
         */
        returnPolicyVerifiedOn?: string | null;
        /**
         * SERV-3020: the vendor's own restocking fee percentage, so settlementForItem
         * (shared/returnSettlement.ts) can use the real figure instead of a flat 15% assumption.
         * Null means the supplier's return policy was never confirmed; optional because a
         * payload predating this carries nothing at all.
         */
        returnRestockingPct?: number | null;
      })
    | null;
  supplierLocation: Pick<
    ProcurementSupplierLocation,
    /* SERV-3014: the depot columns ride along, so the buy modal can say which of the two
     * addresses it picked rather than implying the vendor's buying address is the destination. */
    | "label"
    | "address1"
    | "city"
    | "state"
    | "zipcode"
    | "returnsAddress1"
    | "returnsAddress2"
    | "returnsCity"
    | "returnsState"
    | "returnsZipcode"
  > | null;
  expFulfillmentAt: string | null;
  fulfillmentInfo: ProcurementFulfillmentInfo | null;
  attributes: ProcurementItemAttributes | null;
  paymentStatus: ProcurementPaymentStatus;
  paymentCardId: string | null;
  paymentAmount: string | null;
  paidAt: string | null;
  receiptS3Keys: string[];
  receiptParts: ProcurementReceiptPart[] | null;
  trackingNumbers: ProcurementTrackingNumber[] | null;
  poNumber?: string | null;
  /**
   * SERV-2972. Optional AND nullable at the boundary, and coalesced wherever they are read.
   * Nullable because every one is null on a line that predates the column; optional because a
   * backend deployed before this ticket sends nothing at all, and because the list projections do
   * not carry them. Same reasoning as `totalAnticipatedCost` and `quotationS3Keys` on the request.
   *
   * `unitCost` is the price the officer found per unit. Goods are unitCost x quantity; the line
   * total adds `shippingCost`. They stay apart because freight is what decides whether a line
   * needs a sign off.
   */
  unitCost?: string | null;
  /** When the order was placed. Null on a line ordered before this existed, even if it has a PO. */
  orderedAt?: string | null;
  vendorAccountId?: string | null;
  vendorAccount?: { id: string; accountName: string; paymentTerms: string | null } | null;
  /** What actually turned up. Null means nothing received yet, which is not zero received. */
  receivedQty?: number | null;
  receivedAt?: string | null;
  receivedBy?: string | null;
  receivingNotes?: string | null;
  /** SERV-3071: how it arrived. Null on a line received before this existed. */
  receivingCondition?: ReceivingCondition | null;
  /** The pickup lane's equivalent of a tracking number. */
  pickupReadyAt?: string | null;
  pickupCollectedBy?: string | null;
  pickupCollectedAt?: string | null;
  /**
   * SERV-3497. The approximate speed, then the supplier's confirmed speed or (option null with
   * `etaConfirmedAt` set) date. The window is two UTC-midnight days computed by the server.
   */
  etaApproxOption?: ItemEtaOption | null;
  etaConfirmedOption?: ItemEtaOption | null;
  etaEarliestAt?: string | null;
  etaLatestAt?: string | null;
  etaConfirmedAt?: string | null;
  etaSetAt?: string | null;
  /**
   * The id of this line's own outstanding approval, or null. An item approval holds ONE line; the
   * request's `approvalStatus` is the separate, request-wide mechanism.
   */
  openApprovalId?: string | null;
  /*
   * SERV-2977: the travel booking's own record.
   *
   * Optional AND nullable at the boundary, per the repo's standing rule for a field newly
   * surfaced on an existing table: legacy rows and older list projections can return neither, and
   * a non-null Prisma column does not promise the API sent a value.
   *
   * `refundable` is three-valued and must stay so. Coalescing an unknown to `false` raises a false
   * alarm on the at-risk total; coalescing it to `true` hides a real one.
   *
   * `recordLocator` and `eTicketNumber` are AIRLINE records and are always null on a hotel
   * booking - a hotel reservation has neither.
   */
  tripLegOrder?: number | null;
  confirmationNumber?: string | null;
  recordLocator?: string | null;
  eTicketNumber?: string | null;
  heldUntil?: string | null;
  refundable?: boolean | null;
  changeFee?: string | null;
  freeCancellationUntil?: string | null;
  returnStatus: ReturnStatus | null;
  returnMethod: ReturnMethod | null;
  returnReason?: string | null;
  returnOutcome?: string | null;
  returnNotes?: string | null;
  returnReceiptRef?: string | null;
  returnPhotoS3Keys?: string[];
  returnShippingCost?: string | null;
  returnRestockingFee?: string | null;
  returnCreditAmount?: string | null;
  returnQty?: number | null;
  returnUnitPaid?: string | null;
  returnClientMarkupPct?: string | null;
  returnFault?: string | null;
  returnDeliveredAt?: string | null;
  /** Stamped server-side from the vendor's window; the credit is worth nothing past it. */
  returnDueBy?: string | null;
  /**
   * SERV-2966: what comes off the client's invoice, stamped server side when the return settled.
   *
   * Nullable and optional because it is only written on settlement: a return still in flight, and
   * every return that settled before the column existed, has none. Null is "never settled", which
   * is not the same as a zero credit on a part carrying no markup, so callers must not coalesce
   * the two.
   *
   * Nothing renders this yet, deliberately. Whether a vendor credit reduces what the client is
   * billed is still undecided and nothing performs the reversal, which is why SERV-2885 commented
   * the figure out of the drawer and the settlement form. This ticket persists it so the number
   * stops being lost; displaying or invoicing it is the decision that comes next.
   */
  returnClientCredit?: string | null;
  returnBatchId?: string | null;
  returnClosureOutcome?: string | null;
  returnClosedReason?: string | null;
  returnCarrier?: string | null;
  returnTrackingNumber?: string | null;
  /** Set only when we bought the label ourselves via EasyPost, not when a vendor mails one. */
  returnLabelUrl?: string | null;
  /** The USPS "Label Broker" QR code alternative to the printed label above. */
  returnLabelQrUrl?: string | null;
  returnLabelCost?: string | null;
  returnLabelPurchasedAt?: string | null;
  returnEasypostTrackerId?: string | null;
  /**
   * SERV-3014: the two addresses the label was actually bought against, frozen at purchase, and
   * whether either end departed from what the record holds.
   *
   * Detail only - the list row does not carry them, matching returnTrackerEvents. Optional AND
   * nullable: a label bought before this shipped has neither, and no backfill was possible
   * because the origin was never stored. Null must render as "not recorded", never as an empty
   * address. The flags default false server side, which is the truth for pre-existing rows.
   */
  /**
   * SERV-3014: who asked for this return, and when. Optional AND nullable: every return opened
   * before this shipped has neither, and null means "not recorded" - it must never be rendered
   * as an attribution to whoever last touched the row.
   */
  returnOpenedByName?: string | null;
  returnOpenedAt?: string | null;
  returnLabelShipFrom?: ReturnLabelAddressSnapshot | null;
  returnLabelShipTo?: ReturnLabelAddressSnapshot | null;
  returnLabelShipFromOverridden?: boolean | null;
  returnLabelShipToOverridden?: boolean | null;
  /**
   * SERV-2887: the carrier's own view of the return leg. Optional AND nullable at the boundary:
   * a row whose label predates this change carries a tracker id and nothing else (no backfill was
   * done), so these must render as plain text rather than as a link that goes nowhere.
   * returnTrackerEvents is served as [] rather than null, so "no scans yet" and "nothing stored"
   * read the same to the UI.
   */
  returnTrackerPublicUrl?: string | null;
  returnTrackerStatus?: string | null;
  returnTrackerLastEventAt?: string | null;
  returnTrackerEvents?: ReturnTrackerEvent[] | null;
  /** SERV-2859 AC6/ADDENDUM A1-A2: the QR send-state, nullable - null means "predates this change". */
  returnQrEmailStatus?: ReturnQrEmailStatus | null;
  returnQrEmailSentAt?: string | null;
  returnQrEmailTo?: string | null;
  returnQrEmailFailureReason?: string | null;
  returnTripState?: string | null;
  returnTripBranch?: string | null;
  returnTripArrivedAt?: string | null;
  returnPaidOnCardId?: string | null;
  returnCreditSlipS3Key?: string | null;
  rmaNumber?: string | null;
  returnedAt: string | null;
  createdAt: string;
};

export type ProcurementRequestChildSummary = {
  id: string;
  requestNumber: number;
  childSequence: number | null;
  status: string;
  approvalStatus: string;
  supplierName: string | null;
  officerName: string | null;
};

export type ProcurementRequestParentSummary = {
  id: string;
  requestNumber: number;
  status: string;
};

/** SERV-2975: one pricing line on a queue ticket. `lineNumber` is 1-based, by creation order. */
export interface ProcurementTicketPricingLine {
  id: string;
  lineNumber: number;
  lineItem: string | null;
}

export type ProcurementRequestDetail = {
  /**
   * SERV-2671: the lane, the same value the queue's Procurement Type column shows. Distinct from
   * `category`, which collapses five lanes into PURCHASE_ITEMS - which is why the detail header
   * used to call a phone sale an "Online Purchase".
   */
  lane: ProcurementLane;
  /**
   * SERV-2975: the pricing request this ticket's lane tabs render. Null on every other lane, on
   * PRICING tickets raised before the two were linked, and optional because a backend deployed
   * before SERV-2975 sends nothing at all.
   */
  pricingRequestId?: string | null;
  /** SERV-3353: optional and nullable because a backend deployed before SERV-3353 sends nothing. */
  createdBy?: string | null;
  /**
   * SERV-3353 3.O: whether the server counts the viewer as procurement-side for this request's
   * conversation. Optional and nullable because a backend deployed before 3.O sends nothing.
   */
  viewerIsProcurementSide?: boolean | null;
  /**
   * SERV-2975: every live pricing line on this ticket, in the order raised. A Request for Pricing
   * with several items has one pricing line per item, and `pricingRequestId` names only the first.
   * Optional and nullable because a backend deployed before this change sends nothing.
   */
  pricingLines?: ProcurementTicketPricingLine[] | null;
  /**
   * SERV-2670 supplier split. parentRequest is null unless this request is a child; childRequests
   * is empty unless it is the parent. All three are optional because a backend deployed before the
   * split sends none of them (SERV-2643).
   */
  parentRequest?: ProcurementRequestParentSummary | null;
  childRequests?: ProcurementRequestChildSummary[] | null;
  childSequence?: number | null;
  id: string;
  requestNumber: number;
  tenantId?: string;
  entityName?: string;
  entityAbbreviation?: string | null;
  workOrderId: string;
  /**
   * SERV-2976: the provider assigned to this request's visit, which the vehicle-booking lane checks
   * a driving licence against (`useGetIdDocumentStatus`). Nullable, and optional on top of that:
   * null whenever the request has no visit or nobody has accepted it, and undefined against a
   * backend deployed before this field existed. Both read as "nobody to check", never as a failed
   * check - see `vehicleEligibility.ts`.
   */
  assignedServiceProviderId?: string | null;
  assignedServiceProviderName?: string | null;
  workOrderNumber: string;
  clientPriority?: string | null;
  category: ProcurementCategory;
  status: ProcurementRequestStatus;
  approvalStatus?: ProcurementApprovalStatus;
  requestorRole: string | null;
  /**
   * SERV-2854: the requestor's role at the moment they raised the request, snapshotted on the
   * request row. Distinct from `requestorRole`, which feeds the unrelated "Source" chip. Nullable
   * at the API boundary: legacy rows predate the snapshot.
   */
  requestorRoleAtRaise?: string | null;
  requestorName?: string | null;
  nte: string | null;
  anticipatedCost: string | null;
  /**
   * SERV-2882: the roll-up of every item's anticipated cost, which the backend has always returned
   * on the detail read (procurement/service.ts, `totalAnticipatedCost: sumAnticipatedCost(items)`)
   * and which this type simply never declared. Only the phone sale lanes write the request-level
   * `anticipatedCost`; on every other lane the money lives on the items, so without this the detail
   * screen showed $0.00 for a request the queue displayed a real figure for.
   *
   * Nullable at the boundary on purpose: a request with no items rolls up to nothing, and a backend
   * deployed before this field existed sends nothing at all.
   */
  totalAnticipatedCost?: string | null;
  /**
   * SERV-2882: S3 keys for the quotation or receipt backing this request's spend. At least one is
   * required before the request can be completed.
   *
   * Optional at the boundary rather than a bare `string[]`: a backend deployed before this column
   * existed sends nothing at all, and every reader has to cope with that rather than assume an
   * array. Read it through `quotationKeysOf` rather than indexing it directly.
   */
  quotationS3Keys?: string[] | null;
  notes: string | null;
  /**
   * SERV-2980: the dispatcher's note as written at raise time. `notes` above is also the officer's
   * Edit Request box, so it stops being dispatch's words the first time an officer saves. Null on
   * every request raised before the column, and absent from a backend deployed before it.
   */
  dispatcherNotes?: string | null;
  /**
   * SERV-2972 and SERV-2973 converged on the same field independently: the officer's confirmation
   * that what dispatch submitted is what they are about to act on. Sourcing (SERV-2972) and the
   * rental screen's working tabs (SERV-2973) both unlock on this. Optional AND nullable at the
   * boundary: null on every request raised before this existed, and absent entirely from a backend
   * deployed before it.
   */
  detailsConfirmedAt?: string | null;
  detailsConfirmedBy?: string | null;
  detailsConfirmedByUser?: { firstName: string; lastName: string | null } | null;
  /**
   * SERV-2972: three facts the officer's Request tab shows. Only the online-purchase wizard asks
   * for them, so they are null on every other lane and on every pre-existing row.
   */
  receivingWindow?: string | null;
  costCentre?: string | null;
  budgetCode?: string | null;
  procurementBy: string | null;
  procurementByUser: { firstName: string; lastName: string } | null;
  /**
   * SERV-2854: the assignee's CURRENT role, resolved live on every read - a top-level sibling of
   * `procurementByUser` (BE types.ts:652), not a nested field on it. Never snapshotted, since who
   * owns the request now is a present-tense fact. Optional/nullable: null with no assignee, and a
   * backend deployed before this field existed sends nothing.
   */
  procurementByRole?: string | null;
  beganAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  items: ProcurementRequestItem[];
  /**
   * SERV-2855 AC7 / FR1: computed server-side by the same guards `updateRequest` and `addItems`
   * enforce (service.ts:2461, :2377), so the screen renders this instead of re-deriving it from
   * `status`/`approvalStatus`. The NTE carries its OWN capability and its own reason, separate from
   * the whole-request pair: an approved request has `canEditRequest: true` with `canEditNte: false`
   * (backend types.ts:749-753), so reading the NTE lock off `canEditRequest` unlocks a field the
   * server refuses. All six are optional for the same reason `parentRequest` above is (SERV-2643):
   * a backend deployed before this ticket sends none of them, and absent must read as "not
   * permitted", never as true. See `shared/requestEditCapability.ts`.
   */
  canEditRequest?: boolean;
  canEditNte?: boolean;
  canAddItems?: boolean;
  editRefusalReason?: string | null;
  nteRefusalReason?: string | null;
  addItemsRefusalReason?: string | null;
  /**
   * SERV-2977: findings across this request's items, derived server side so the queue and the
   * detail page cannot disagree. Optional at the boundary and defaulted to [] by every reader:
   * absent means an older backend, which must read as "no findings", never as a crash.
   */
  tripChecks?: ProcurementTripCheck[] | null;
  /**
   * SERV-2977: the travel gate. Null on every non-travel request and until an officer accepts
   * what dispatch typed; nothing on the lane may be priced or booked while it is null.
   *
   * Optional as well as nullable, per the standing rule for a field newly surfaced on an existing
   * table - an older backend sends none of the three, and that must read as "not confirmed".
   */
  travelDetailsConfirmedAt?: string | null;
  travelDetailsConfirmedBy?: string | null;
  travelDetailsConfirmedByName?: string | null;
  /**
   * SERV-2977: when the traveller was sent the confirmations, and where. Null means not sent,
   * which is the correct reading of every request predating this.
   */
  itinerarySentAt?: string | null;
  itinerarySentTo?: string | null;
};

/**
 * SERV-2889: where a return ships FROM. Carried on the item rather than read from the work-order
 * module, which 403s for the Procurement Officer role (granted `procurement`, not `work_order`).
 * Every part is nullable, and the object itself is nullable: a work order location can exist with
 * an empty address row, which is precisely the case the buy-label screen refuses on.
 */
export type ProcurementJobSite = {
  siteId: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
};

export type ProcurementItemDetail = ProcurementRequestItem & {
  requestNumber: number;
  entityName?: string;
  entityAbbreviation?: string | null;
  workOrderId: string;
  workOrderNumber: string;
  category: ProcurementCategory;
  requestStatus: ProcurementRequestStatus;
  approvalStatus?: ProcurementApprovalStatus | null;
  /** The HIGH end of the request's cost range ("Not to exceed"). */
  nte: string | null;
  /**
   * SERV-2959. The LOW end of the same range ("Estimated from"). PROC-6 collects the two together
   * as one range for the WHOLE request, so neither is this item's own price - `anticipatedCost`
   * above is, and it is routinely null on a phone sale priced by quotation total rather than per
   * unit. Nullable at the boundary: requests raised before this shipped return it, but a lane that
   * collects no range leaves it null, and a list projection deployed ahead of the mapper returns
   * undefined.
   */
  requestAnticipatedCost?: string | null;
  jobSite?: ProcurementJobSite | null;
};

// ── Requests / responses ────────────────────────────────────────────────────

export type ProcurementListResult<T> = {
  data: T[];
  pagination: APPagination;
};

export type ProcurementRequestListResult = ProcurementListResult<ProcurementRequestListItem>;
export type ProcurementItemListResult = ProcurementListResult<ProcurementItemListRow>;
export type ProcurementSupplierListResult = ProcurementListResult<ProcurementSupplier>;

export type ProcurementListQueryOptions = {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  filters?: FilterEntry[];
  /** Fixed scoping merged into filterBy on top of user filters. */
  workOrderId?: string;
  category?: ProcurementCategory;
  statuses?: ProcurementRequestStatus[] | ProcurementItemStatus[];
  itemTypes?: ProcurementItemType[];
  requestStatuses?: ProcurementRequestStatus[];
  queueView?: ProcurementRequestQueueView;
  /** SERV-2632. The Procurement Type filter; several lanes at once are an OR. */
  lanes?: ProcurementLane[];
  returnsOnly?: boolean;
  /** SERV-2859 AC2.2/ADDENDUM A7: the queue's "Returns" filter, sent as a bare scalar, not an array. */
  returnState?: ProcurementReturnState;
  /** SERV-3355: the queue's Assigned Officer chip. OR'd with `unassigned` server-side. */
  procurementBy?: string[];
  /** SERV-3355: requests with no assigned officer; OR'd with `procurementBy`. */
  unassigned?: boolean;
  /** SERV-3504: the queue's Status chip, on the DERIVED status the row shows. Several are an OR. */
  derivedStatuses?: ProcurementDerivedStatus[];
  /** SERV-3504: the queue's Work Order chip. Several are an OR. */
  workOrderIds?: string[];
  /** SERV-2859 AC4.7: pre-filters the returns screen from the queue badge / Return Items deep link. */
  requestId?: string;
  tenantId?: string;
};

export type CreateProcurementItemInput = {
  itemType: ProcurementItemType;
  name?: string;
  url?: string;
  quantity?: number;
  anticipatedCost?: number;
  fulfillmentMethod?: FulfillmentMethod;
  supplierId?: string;
  supplierLocationId?: string;
  expFulfillmentAt?: string;
  fulfillmentInfo?: ProcurementFulfillmentInfo;
  attributes?: ProcurementItemAttributes;
  /** SERV-3165: which leg of a flight trip this is, 0 outbound and 1 the return. Flights only. */
  tripLegOrder?: number;
};

export type CreateProcurementRequestPayload = {
  workOrderId: string;
  category: ProcurementCategory;
  nte?: number;
  anticipatedCost?: number;
  notes?: string;
  requestorRole?: string;
  /** Set when the request was raised from a specific visit on the work order. */
  visitId?: string;
  /**
   * SERV-2972: the receiving window, cost centre and budget code the officer's Request tab shows.
   * Optional because only the online-purchase wizard asks for them.
   */
  receivingWindow?: string | null;
  costCentre?: string | null;
  budgetCode?: string | null;
  /**
   * SERV-2980: the dispatcher's quotation, filed on the request at raise time rather than on each
   * line, where Complete sale's own quotation overwrote it.
   */
  quotationS3Keys?: string[];
  items: CreateProcurementItemInput[];
};

export type UpdateProcurementRequestPayload = {
  nte?: number | null;
  notes?: string | null;
  /** SERV-2855: the API already accepts it (BE validation.ts:123); the request-detail nte guard
   * (D1) never applies to this field. */
  anticipatedCost?: number | null;
  /**
   * SERV-2972: the receiving window, cost centre and budget code the officer's Request tab shows.
   * Optional because only the online-purchase wizard asks for them.
   */
  receivingWindow?: string | null;
  costCentre?: string | null;
  budgetCode?: string | null;
  /** SERV-3354: why a COMPLETED request is being changed. The server requires it there. */
  changeNote?: string;
};

/**
 * SERV-3353: the creator's corrections to a returned request, in one POST. A POST because the
 * Dispatcher role holds procurement view and create only (SERV-2886), and PATCH maps to edit.
 */
export type SubmitCorrectionsPayload = {
  request?: UpdateProcurementRequestPayload;
  /** SERV-3353 3.F: supplierLocationId null clears the location. */
  items?: (Pick<
    UpdateProcurementItemPayload,
    "name" | "url" | "quantity" | "anticipatedCost" | "supplierId" | "supplierLocationId"
  > & {
    id: string;
    /** SERV-3353 3.G: a typed address instead of a branch; null clears it. */
    supplierLocationAddress?: string | null;
    /** SERV-3353 3.I: a typed supplier name instead of a record; null clears it. */
    supplierName?: string | null;
  })[];
  /** SERV-3353 3.F: lines the dispatcher adds while correcting, at most 20. */
  newItems?: CorrectionNewItem[];
};

export type CorrectionNewItem = {
  name: string;
  quantity: number;
  url?: string;
  anticipatedCost?: number;
  supplierId?: string;
  supplierLocationId?: string;
  /** SERV-3353 3.G: a typed address instead of a branch. */
  supplierLocationAddress?: string;
  /** SERV-3353 3.I: a typed supplier name instead of a record. */
  supplierName?: string;
};

/** SERV-2972: the officer's details check. `confirmed: false` reopens it. */
export type ConfirmProcurementRequestDetailsPayload = {
  confirmed: boolean;
};

export type AddProcurementRequestItemsPayload = {
  items: CreateProcurementItemInput[];
  /** SERV-3354: why items are being added to a COMPLETED request. The server requires it there. */
  changeNote?: string;
};

export type AssignProcurementRequestPayload = {
  /** The officer's user id. Null unassigns the request. */
  procurementBy: string | null;
};

export type BulkAssignProcurementRequestsPayload = {
  requestIds: string[];
  /** The officer's user id. Null unassigns every listed request. */
  procurementBy: string | null;
};

export type BulkAssignProcurementRequestsResult = {
  updated: number;
};

/** Why claiming a request did not also start it - present only when `begun` is false. */
export const PROCUREMENT_CLAIM_SKIPPED_REASONS = [
  "NOT_APPROVED",
  "ALREADY_STARTED",
  "ON_HOLD",
] as const;
export type ProcurementClaimSkippedReason = (typeof PROCUREMENT_CLAIM_SKIPPED_REASONS)[number];

/**
 * SERV-2858. PATCH /procurement/request/:id/claim assigns the caller and, in the same transaction,
 * attempts to begin the request - the response is the updated `ProcurementRequestDetail` with the
 * begin outcome spread on top, matching how assignRequest/beginRequest already return the detail
 * flat rather than nested. `begun`/`itemsBegun` are typed optional rather than assumed present -
 * same boundary caution as everywhere else a newly-added response field meets an older caller.
 */
export type ClaimProcurementRequestResult = ProcurementRequestDetail & {
  begun?: boolean;
  beginSkippedReason?: ProcurementClaimSkippedReason;
  itemsBegun?: number;
};

export type BulkClaimProcurementRequestsPayload = {
  requestIds: string[];
};

export type BulkClaimProcurementRequestsResult = {
  updated: number;
  begun: number;
  itemsBegun?: number;
  skipped: { requestId: string; reason: ProcurementClaimSkippedReason }[];
};

export type UpdateProcurementItemPayload = Omit<
  Partial<CreateProcurementItemInput>,
  "itemType" | "supplierId" | "supplierLocationId" | "url" | "anticipatedCost" | "expFulfillmentAt"
> & {
  /**
   * SERV-3360: nullable on UPDATE, as `UpdateItemSchema` declares them (procurement/validation.ts),
   * so the purchase record can clear a stored value. Omitted still leaves it alone.
   */
  url?: string | null;
  anticipatedCost?: number | null;
  expFulfillmentAt?: string | null;
  poNumber?: string | null;
  /** SERV-2671 FR1: shipping is entered per item, not once per order. */
  shippingCost?: number | null;
  shippingCurrency?: string | null;
  /** SERV-2972: the sourcing pair. `orderedAt` is NOT here - the server stamps it. */
  unitCost?: number | null;
  vendorAccountId?: string | null;
  /**
   * SERV-2976: nullable on UPDATE, unlike on create.
   *
   * `UpdateItemSchema` declares both `.nullable().optional()` (procurement/validation.ts:299-300),
   * so the server accepts an explicit null and clears the link. Inheriting the create shape made
   * them `string | undefined` here, which is the narrower of the two and cannot express "no longer
   * this supplier" - and omitting the key instead leaves the old supplier in place, since a PATCH
   * only writes what it is given.
   */
  supplierId?: string | null;
  supplierLocationId?: string | null;
  /*
   * SERV-2977: omitted leaves the stored value alone, explicit null clears it - the backend
   * distinguishes the two with `!== undefined`. `changeFee` is a number on the way in and a
   * string on the way back, like every other money field on this boundary.
   */
  tripLegOrder?: number | null;
  confirmationNumber?: string | null;
  recordLocator?: string | null;
  eTicketNumber?: string | null;
  heldUntil?: string | null;
  refundable?: boolean | null;
  changeFee?: number | null;
  freeCancellationUntil?: string | null;
};

/** SERV-2977: one finding from the backend's `deriveTripChecks`. */
export type ProcurementTripCheckSeverity = "BLOCKER" | "WARNING";

export type ProcurementTripCheck = {
  code: string;
  severity: ProcurementTripCheckSeverity;
  itemId: string | null;
  title: string;
  detail: string;
};

/** SERV-3071: how a received item arrived. Mirrors RECEIVING_CONDITIONS in the BE validation. */
export type ReceivingCondition = "GOOD" | "DAMAGED" | "WRONG_ITEM" | "SHORT_COUNT";

/**
 * SERV-2972: what actually arrived.
 *
 * `receivedQty` may be below the ordered quantity - that is the normal case on this lane, and it
 * leaves the line open for the balance rather than completing it.
 */
export type ReceiveProcurementItemPayload = {
  receivedQty: number;
  receivedBy: string;
  receivedAt?: string;
  receivingNotes?: string | null;
  receivingCondition?: ReceivingCondition | null;
  pickupCollectedBy?: string | null;
};

/** SERV-2972: the pickup lane's equivalent of adding a tracking number. */
export type SetProcurementItemPickupPayload = {
  pickupReadyAt: string;
  pickupCollectedBy?: string | null;
};

/**
 * SERV-2508 adds ON_PHONE and REQUESTED. REQUESTED is an action here only as the way back out of
 * ON_PHONE when a call should not have been started; the backend's ITEM_STATUS_ALLOWED_FROM allows
 * it from ON_PHONE and nowhere else, so this cannot reopen a fulfilled or cancelled line.
 */
export type ProcurementItemStatusAction = Extract<
  ProcurementItemStatus,
  | "REQUESTED"
  | "ON_PHONE"
  | "PICKED_UP"
  | "DELIVERED"
  | "CHECKED_IN"
  | "CHECKED_OUT"
  | "FLOWN"
  | "COLLECTED"
  | "MOVED_IN"
  | "ORDERED"
  | "COMPLETED"
  | "CANCELLED"
>;

export const ITEM_ETA_OPTIONS = [
  "OVERNIGHT",
  "DAYS_2_5",
  "DAYS_5_10",
  "WEEKS_2_4",
  "BACKORDERED",
] as const;
export type ItemEtaOption = (typeof ITEM_ETA_OPTIONS)[number];

export type SetProcurementItemEtaPayload =
  | { approx: ItemEtaOption; tz: string }
  | { confirm: { option: Exclude<ItemEtaOption, "BACKORDERED"> } | { date: string }; tz: string };

export type LogProcurementItemPaymentPayload = {
  paymentCardId: string;
  amount: number;
};

export type UpdateProcurementItemReceiptPayload = {
  receiptS3Keys?: string[];
  receiptParts?: ProcurementReceiptPart[];
};

export type UpdateProcurementItemTrackingPayload = {
  trackingNumbers: ProcurementTrackingNumber[];
};

export type UpdateProcurementItemReturnPayload = {
  returnStatus: ReturnStatus;
  returnMethod?: ReturnMethod;
  returnReason?: ReturnReason;
  returnOutcome?: ReturnOutcome;
  returnNotes?: string;
  returnReceiptRef?: string;
  returnPhotoS3Keys?: string[];
  returnShippingCost?: number;
  returnRestockingFee?: number;
  returnCreditAmount?: number;
  returnQty?: number;
  returnUnitPaid?: number;
  returnClientMarkupPct?: number;
  returnFault?: "VENDOR" | "US" | "CLIENT" | "UNKNOWN";
  returnDeliveredAt?: string;
  returnBatchId?: string;
  returnClosureOutcome?: "CREDITED" | "REPLACED" | "REFUSED" | "LOST" | "ABANDONED" | "EXPIRED";
  returnClosedReason?: string;
  returnCarrier?: string;
  returnTrackingNumber?: string;
  returnTripState?: "PLANNED" | "EN_ROUTE" | "AT_STORE" | "DROPPED" | "CONFIRMED" | "FAILED";
  returnTripBranch?: string;
  returnTripArrivedAt?: string;
  returnPaidOnCardId?: string;
  returnCreditSlipS3Key?: string;
  rmaNumber?: string;
};

/** SERV-3014: one shape for both ends, so an override of either is described identically. */
export type ReturnLabelAddressPayload = {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
};

export type BuyProcurementReturnLabelPayload = {
  /**
   * SERV-3014: where the parcel actually is, when that is not the work order's job site - the
   * item is back at the workshop, say. Omit to ship from the job site. Supplying this skips the
   * job-site lookup entirely, so a job site with no address stops blocking the purchase.
   */
  fromAddress?: ReturnLabelAddressPayload;
  /**
   * SERV-3014: this changed meaning. It used to be a first capture only, supplied when the
   * supplier location had no structured address, and was silently DISCARDED whenever one was on
   * file. It is now an override the backend honours over anything stored - and, when the location
   * has no address at all, still the first capture that gets saved onto it.
   */
  toAddress?: ReturnLabelAddressPayload;
  parcel: {
    weightOz: number;
    lengthIn?: number;
    widthIn?: number;
    heightIn?: number;
  };
};

export type CreateProcurementSupplierPayload = {
  name: string;
  websiteUrl: string;
  phoneNumber?: string;
  locationLabel?: string;
  tier?: ProcurementSupplierTier;
};

export type UpdateProcurementSupplierPayload = {
  name?: string;
  websiteUrl?: string;
  phoneNumber?: string | null;
  accountNumber?: string | null;
  tier?: ProcurementSupplierTier;
};

export type InferProcurementSupplierPayload = {
  url: string;
};

export type ProcurementSupplierInference = {
  supplier: ProcurementSupplier;
  supplierCreated: boolean;
  itemTypeGuess: ProcurementItemType | null;
};

export type CreateProcurementSupplierLocationPayload = {
  label: string;
  address?: string;
  address1?: string;
  city?: string;
  state?: string;
  zipcode?: string;
};

export type CreateProcurementPaymentCardPayload = {
  nameOnCard: string;
  brand: CardBrand;
  last4: string;
  /** Main-app logins only: which entity owns the card (from the request being paid). */
  tenantId?: string;
  /** SERV-2638: hold the card against this supplier, so the next phone sale there offers it. */
  supplierId?: string;
};

// ── Part search ─────────────────────────────────────────────────────────────

export type PartSearchQuery = {
  q: string;
  /** Catalog / quote page captured with the line item, when the requester supplied one. */
  sourceUrl?: string;
  /** Work order the request belongs to; the service measures branch distance from its site address. */
  woId?: string;
};

/** Vendor branch that stocks the part, measured against the work order's site address. */
export type PartSearchPickupLocation = {
  name?: string;
  address?: string;
  distanceMiles?: number;
};

/**
 * Mirrors the `fulfillmentMethods` option set the rest of the procurement module already
 * speaks, so the method an officer picks on a search result carries straight into the
 * request without a second translation step.
 */
export const PART_SEARCH_FULFILLMENT_METHODS = [
  "pickup",
  "order-ship",
  "phone-sale",
  "sp-reimburse",
] as const;

export type PartSearchFulfillmentMethod = (typeof PART_SEARCH_FULFILLMENT_METHODS)[number];

/** One way to actually get this part from this vendor; a vendor can offer several. */
export type PartSearchFulfillmentOption = {
  method: PartSearchFulfillmentMethod;
  /** Vendor-quoted timing for this method alone, e.g. "today at 3 branches". */
  leadTime?: string;
  /** Landed price for this method when it differs from the listed price (freight, will-call). */
  price?: number | null;
};

export type PartSearchResult = {
  title: string;
  vendor: string;
  price: number | null;
  currency: string;
  url: string;
  partNumber?: string | null;
  availability?: string | null;
  imageUrl?: string | null;
  /** Absent for ship-only vendors, which have no counter to pick up from. */
  pickupLocation?: PartSearchPickupLocation;
  /** Absent from services that predate the field; the card then derives a single method. */
  fulfillmentOptions?: PartSearchFulfillmentOption[];
};

export type PartSearchPayload = {
  results: PartSearchResult[];
};

// ── Tracking verification ────────────────────────────────────────────────────

export const TRACKING_VERIFICATION_CARRIERS = ["ups", "fedex", "usps", "dhl"] as const;
export type TrackingVerificationCarrier = (typeof TRACKING_VERIFICATION_CARRIERS)[number];

export type VerifyTrackingQuery = {
  trackingCode: string;
  carrier: TrackingVerificationCarrier;
};

/** `status` is EasyPost's own tracker status (e.g. "in_transit") when confirmed, else null. */
export type TrackingVerificationResult = {
  valid: boolean;
  status: string | null;
};

// ── Standalone return label (no procurement item behind it — the demo return panel) ────────

export type StandaloneEasypostAddress = {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
};

// ── Facility search ──────────────────────────────────────────────────────────

/**
 * SERV-2626: "hotel" is added here for the wizard's nearest-hotel search, but the backend does
 * not accept it yet - `onengine-backend/src/modules/procurement/validation.ts:435` validates
 * `kind` against `z.enum(PROCUREMENT_FACILITY_KINDS)`, and that array
 * (`onengine-backend/.../types.ts:735`) is still `["storage","equipment","vehicle"]`. A live
 * hotel search returns 400 until that backend enum is updated; this is a known, separately
 * tracked gap, not something to work around here.
 */
export type FacilitySearchKind = "storage" | "equipment" | "vehicle" | "hotel";

export type FacilitySearchProvider = {
  provider: string;
  website: string;
};

export type FacilitySearchQuery = {
  kind: FacilitySearchKind;
  origin: string;
  assetLabel: string;
  providers: FacilitySearchProvider[];
};

export const FACILITY_SEARCH_AVAILABILITY = ["available", "waitlist", "unknown"] as const;
export type FacilitySearchAvailability = (typeof FACILITY_SEARCH_AVAILABILITY)[number];

/** One provider's nearest branch, or a miss when none could be confirmed nearby. */
export type FacilitySearchResult = {
  provider: string;
  found: boolean;
  siteName: string | null;
  address: string | null;
  phone: string | null;
  monthly: number | null;
  milesApprox: number | null;
  hours: string | null;
  availability: FacilitySearchAvailability;
  sourceUrl: string | null;
};

export type FacilitySearchPayload = {
  results: FacilitySearchResult[];
};

// ── Quotation extraction (SERV-2673) ─────────────────────────────────────────

export type QuotationExtractionQuery = {
  s3Key: string;
  fileName: string;
};

/**
 * One line item off the quotation, goods only - the backend already filters out delivery/pickup
 * charge lines. Every field is nullable for the same reason as the top-level result below: a miss
 * is extraction failing to read that one value, not an error.
 */
export type QuotationExtractionLineItem = {
  description: string | null;
  sku: string | null;
  quantity: number | null;
  unit: string | null;
  unitPrice: number | null;
  lineTotal: number | null;
};

/**
 * Every field is nullable: extraction failing to read a given value off the quotation is an
 * expected outcome, not an error case, so a miss on any one field must never be typed as if the
 * backend guaranteed it.
 */
export type QuotationExtractionResult = {
  supplier: string | null;
  supplierAddress: string | null;
  supplierPhone: string | null;
  orderNumber: string | null;
  subtotal: number | null;
  discount: number | null;
  tax: number | null;
  shipping: number | null;
  total: number | null;
  issues: string[];
  rawText: string | null;
  items: QuotationExtractionLineItem[];
};

// ── Return policy search ─────────────────────────────────────────────────────

export type ReturnPolicySearchQuery = {
  vendor: string;
  /** Already-known policy page, if any, so the search can verify it rather than start blind. */
  knownPolicyUrl?: string | null;
};

/**
 * SERV-3014: one end of a label as it was actually bought, frozen at purchase.
 *
 * Deliberately not a reference to the vendor location or the job site: those move, and a job site
 * edited after a label shipped would otherwise silently rewrite where that parcel went.
 */
export type ReturnLabelAddressSnapshot = {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  phone?: string;
};

export const RETURN_POLICY_FREIGHT_PAYERS = ["vendor", "buyer"] as const;
export type ReturnPolicyFreightPayer = (typeof RETURN_POLICY_FREIGHT_PAYERS)[number];

/** A vendor's return policy as confirmed by a live search just now, or a miss when it couldn't be. */
export type ReturnPolicySearchResult = {
  found: boolean;
  windowDays: number | null;
  restockingPct: number | null;
  rmaRequired: boolean | null;
  originatingBranchOnly: boolean | null;
  returnsDeskHours: string | null;
  refundsToOriginalCardOnly: boolean | null;
  returnFreightPaidBy: ReturnPolicyFreightPayer | null;
  nonReturnable: string[];
  policyUrl: string | null;
};

// ── Pricing requests ─────────────────────────────────────────────────────────

export const PRICING_REQUEST_STATUSES = [
  "REQUESTED",
  "IN_PROGRESS",
  "AWAITING_SP",
  "RETURNED",
  "EXPIRED",
  "CANCELLED",
] as const;
export type PricingRequestStatus = (typeof PRICING_REQUEST_STATUSES)[number];

export const PRICING_REQUEST_STATUS_LABELS: Record<PricingRequestStatus, string> = {
  REQUESTED: "Requested",
  IN_PROGRESS: "In Progress",
  AWAITING_SP: "Awaiting SP Info",
  RETURNED: "Returned",
  EXPIRED: "Expired",
  CANCELLED: "Cancelled",
};

export const OPEN_PRICING_REQUEST_STATUSES: PricingRequestStatus[] = [
  "REQUESTED",
  "IN_PROGRESS",
  "AWAITING_SP",
];

export const PRICING_NEEDED_BY_OPTIONS = [
  { value: "1h", label: "Within 1 hour" },
  { value: "4h", label: "Within 4 hours" },
  { value: "eod", label: "End of day" },
  { value: "tomorrow-9", label: "Tomorrow 9:00 AM" },
  { value: "24h", label: "Within 24 hours" },
  { value: "48h", label: "Within 48 hours" },
] as const;
export type PricingNeededBy = (typeof PRICING_NEEDED_BY_OPTIONS)[number]["value"];

export const pricingNeededByLabel = (code: string): string =>
  PRICING_NEEDED_BY_OPTIONS.find((o) => o.value === code)?.label ?? code;

export type PricingFulfillmentChoice = {
  method: string;
  vendorName: string;
  productUrl: string;
  unitPrice: number | null;
  currency: string;
  leadTime?: string;
  branchName?: string | null;
  branchAddress?: string | null;
  distanceMiles?: number;
};

export type PricingRequestListItem = {
  id: string;
  requestNumber: number;
  /**
   * SERV-2975: the queue ticket this was raised from, whose lane tabs render it. Null only on
   * requests raised before the two were linked and never backfilled, which open the standalone
   * screen instead.
   */
  requestId: string | null;
  /**
   * SERV-3169: the linked queue ticket's number and split suffix. A linked pricing request is
   * called by its ticket everywhere; `requestNumber` is the pricing row's own counter, shown only
   * when there is no ticket. Null when `requestId` is.
   */
  queueRequestNumber: number | null;
  queueChildSequence: number | null;
  workOrderId: string;
  workOrderNumber: string;
  clientName: string;
  entityName: string;
  entityAbbreviation: string | null;
  lineItem: string;
  spec: string | null;
  neededBy: string;
  neededByAt: string;
  escalated: boolean;
  status: PricingRequestStatus;
  requestedBy: string;
  requestedByUser: { firstName: string; lastName: string | null } | null;
  claimedBy: string | null;
  claimedByUser: { firstName: string; lastName: string | null } | null;
  createdAt: string;
  /**
   * Identity of the proposal this request is priced into, so the link to it can be named rather than
   * reading "Open proposal" on every row. Optional: the list endpoint may not send them yet, and the
   * link falls back to the generic label when it doesn't.
   */
  proposalNumber?: number | null;
  proposalName?: string | null;
};

/** SERV-2975: one priced option on a pricing request, the officer's unit of comparison. */
export type PricingOption = {
  id: string;
  pricingRequestId: string;
  vendorName: string;
  make: string | null;
  model: string | null;
  serialNumber: string | null;
  sourceLink: string | null;
  vendorCost: string;
  marginPct: string;
  shippingCost: string | null;
  clientPrice: string;
  shipLeadTime: string | null;
  leadDays: number | null;
  fulfillment: PricingFulfillmentChoice | null;
  findings: string | null;
  quoteS3Keys: string[];
  createdBy: string;
  createdByUser: { firstName: string; lastName: string | null } | null;
  createdAt: string;
};

/**
 * SERV-2975: how the line reached us. Derived server-side from the request's own columns, so the
 * officer's first question - was this an upload, a link, or typed in by hand - is answered by the
 * data rather than guessed from it.
 */
export const PRICING_SOURCE_KIND_LABELS: Record<PricingSourceKind, string> = {
  upload: "Quote uploaded",
  link: "Pasted link",
  manual: "Typed in",
};
export type PricingSourceKind = "upload" | "link" | "manual";

export type PricingRequestDetail = PricingRequestListItem & {
  tenantId: string;
  intent: PricingRequestIntent | null;
  currentEta: string | null;
  localSourcingAddress: string | null;
  anticipatedCost: string | null;
  // SERV-2975: the rest of what dispatch submitted, for the officer's check.
  quantity: number | null;
  lineMake: string | null;
  lineModel: string | null;
  sourceUrl: string | null;
  attachmentS3Keys: string[] | null;
  sourceKind: PricingSourceKind;
  detailsCheckedBy: string | null;
  detailsCheckedByUser: { firstName: string; lastName: string | null } | null;
  detailsCheckedAt: string | null;
  options: PricingOption[];
  recommendedOptionId: string | null;
  recommendationReason: string | null;
  optionsSentAt: string | null;
  decidedOptionId: string | null;
  decidedBy: string | null;
  decidedByUser: { firstName: string; lastName: string | null } | null;
  decidedAt: string | null;
  decisionNote: string | null;
  noOptionFoundAt: string | null;
  noOptionReason: string | null;
  vendorCost: string | null;
  marginPct: string | null;
  shippingCost: string | null;
  shipLeadTime: string | null;
  vendorName: string | null;
  make: string | null;
  model: string | null;
  serialNumber: string | null;
  sourceLink: string | null;
  fulfillment: PricingFulfillmentChoice | null;
  findings: string | null;
  clientPrice: string | null;
  claimedAt: string | null;
  awaitingSpAt: string | null;
  returnedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  /** SERV-3360: projected from the linked item's attributes; absent on an older API. */
  teamLeaders?: ProcurementTeamLeader[] | null;
};

export type PricingRequestListResult = {
  data: PricingRequestListItem[];
  pagination: APPagination;
};

export type PricingRequestListQueryOptions = {
  page: number;
  pageSize: number;
  search?: string;
  filterBy?: {
    statuses?: PricingRequestStatus[];
    escalatedOnly?: boolean;
    claimedBy?: string;
    workOrderId?: string;
  };
};

export type CreatePricingRequestPayload = {
  workOrderId: string;
  lineItem: string;
  spec?: string;
  neededBy: string;
  customNeededByAt?: string;
  intent?: PricingRequestIntent;
  currentEta?: string;
  localSourcingAddress?: string;
  anticipatedCost?: number;
};

export type ConfirmPricingDetailsPayload = { note?: string };
export type QueryPricingDetailsPayload = { note: string };

export type AddPricingOptionPayload = {
  vendorName: string;
  make?: string;
  model?: string;
  serialNumber?: string;
  sourceLink?: string;
  vendorCost: number;
  marginPct: number;
  shippingCost?: number;
  shipLeadTime?: string;
  fulfillment?: PricingFulfillmentChoice;
  findings?: string;
  quoteS3Keys?: string[];
};

export type SendPricingOptionsPayload = {
  recommendedOptionId: string;
  recommendationReason: string;
};

export type RecordPricingDecisionPayload = {
  decidedOptionId: string;
  decisionNote?: string;
};

export type NoPricingOptionFoundPayload = { noOptionReason: string };

export type SubmitPricingQuotePayload = {
  vendorCost: number;
  marginPct: number;
  shippingCost?: number;
  shipLeadTime?: string;
  vendorName: string;
  make?: string;
  model?: string;
  serialNumber?: string;
  sourceLink?: string;
  fulfillment?: PricingFulfillmentChoice;
  findings?: string;
};

export type CancelPricingRequestPayload = {
  cancelReason?: string;
};

// ── Flight search ────────────────────────────────────────────────────────────

export const TRAVEL_CABINS = ["economy", "premium-economy", "business", "first"] as const;
export type TravelCabin = (typeof TRAVEL_CABINS)[number];

/** A carrier to look up, with the site the search should read its fares from. */
export type FlightSearchCarrier = {
  carrier: string;
  website: string;
};

export type FlightSearchQuery = {
  /** IATA codes, because that is what every carrier's API takes. */
  origin: string;
  destination: string;
  departOn: string;
  returnOn: string | null;
  cabin: TravelCabin;
  /**
   * Which carriers to price, a batch at a time. Named rather than left open because the search reads
   * each carrier's own site: an open-ended "find me flights" has nowhere to look and nothing to cite.
   */
  carriers: FlightSearchCarrier[];
  /**
   * Trades jobs slip, so a fare that cannot be moved is often the expensive one. On by default at the
   * call site rather than here, so the caller has to say what it wants.
   */
  changeableOnly: boolean;
};

/** One carrier's best fare for the trip, or a miss when nothing could be confirmed. */
export type FlightSearchResult = {
  carrier: string;
  found: boolean;
  /**
   * No live GDS/booking integration exists behind this search, only a web-search read of the
   * carrier's own page, so this is always null — there is no bookable offer to hand back, only a
   * priced fare. Never gate on this being present; gate on `found` and `total` instead.
   */
  offerId: string | null;
  flightNumbers: string[];
  departAt: string | null;
  arriveAt: string | null;
  /**
   * Total scheduled duration in minutes, as published by the carrier. Not derived from
   * `departAt`/`arriveAt`, which are local times and cross time zones on most US routes, so
   * diffing them would silently under- or over-count the real flight time.
   */
  durationMinutes: number | null;
  stops: number | null;
  total: number | null;
  changeable: boolean | null;
  changeFee: number | null;
  bagsIncluded: number | null;
  /** When the airline releases an unpaid seat. Null means the fare has to be bought outright. */
  holdUntil: string | null;
  sourceUrl: string | null;
  /**
   * True when the search is illustrating the shape of an answer rather than reporting a fare it
   * verified. The local mock sets it, and a real search should set it too rather than hand back a
   * price it cannot stand behind.
   */
  unverified?: boolean;
};

export type FlightSearchPayload = {
  results: FlightSearchResult[];
};

/* ── SERV-2973: the rental agreement ───────────────────────────────────────────────────────────
 *
 * The money model on a rental lives in real columns from this ticket on. Everything below mirrors
 * `procurement_rental_agreements`, and every money field is typed `string | null` because the API
 * serialises Decimal columns as strings and a branch that quotes only a weekly rate genuinely
 * leaves the other tiers null. Coalesce at the boundary; do not assume the column's presence in
 * the schema means a value comes back.
 */

export type RentalBilledUnit = "hours" | "days" | "weeks" | "months";

export type ProcurementRentalExtension = {
  id: string;
  fromEndOn: string;
  toEndOn: string;
  agreedWith: string | null;
  confirmationRef: string | null;
  reason: string | null;
  addedAmount: string | null;
  createdAt: string | null;
};

export type ProcurementRentalAgreement = {
  id: string;
  itemId: string;
  requestId: string;

  supplierId: string | null;
  supplierName: string | null;
  branchName: string | null;
  branchPhone: string | null;
  vendorAccountId: string | null;
  accountLabel: string | null;

  agreementNumber: string | null;
  quotationNumber: string | null;
  soNumber: string | null;

  billedUnit: RentalBilledUnit | null;
  dailyRate: string | null;
  weeklyRate: string | null;
  monthlyRate: string | null;
  waiverPerDay: string | null;
  deliveryCharge: string | null;
  collectionCharge: string | null;
  depositAmount: string | null;
  depositReleased: boolean | null;
  /** SERV-3100: when the deposit came back, and who recorded it. Null while held, and on older rows. */
  depositReleasedAt?: string | null;
  depositReleasedBy?: string | null;

  startOn: string | null;
  endOn: string | null;

  collectFrom: string | null;
  collectAt: string | null;
  meterAtPickup: string | null;
  offRentMethod: string | null;
  certConfirmed: boolean | null;
  operatorName: string | null;
  quotationS3Keys: string[] | null;

  onHireAt: string | null;
  offRentAt: string | null;
  offRentCallLog: string | null;
  meterAtReturn: string | null;
  returnCondition: string | null;
  damageCharge: string | null;

  invoiceNumber: string | null;
  invoiceAmount: string | null;
  invoiceS3Keys: string[] | null;
  settledAt: string | null;

  notes: string | null;
  extensions: ProcurementRentalExtension[] | null;
};

export type BookRentalAgreementPayload = {
  supplierId?: string;
  supplierName?: string;
  branchName: string;
  branchPhone?: string;
  vendorAccountId?: string;
  accountLabel?: string;
  agreementNumber: string;
  quotationNumber?: string;
  soNumber?: string;
  billedUnit: RentalBilledUnit;
  dailyRate?: number;
  weeklyRate?: number;
  monthlyRate?: number;
  waiverPerDay?: number;
  deliveryCharge?: number;
  collectionCharge?: number;
  depositAmount?: number;
  startOn: string;
  endOn: string;
  collectFrom?: string;
  /** SERV-2973: when the kit is picked up. ISO instant; a pickup is arranged for a time of day. */
  collectAt?: string;
  meterAtPickup?: string;
  offRentMethod?: string;
  certRequired?: boolean;
  certConfirmed?: boolean;
  operatorName?: string;
  quotationS3Keys?: string[];
  notes?: string;
};

export type ExtendRentalAgreementPayload = {
  endOn: string;
  agreedWith: string;
  /** SERV-3100: optional; not every branch reads back a new reference when it extends a hire. */
  confirmationRef?: string;
  reason?: string;
};

export type StartRentalHirePayload = {
  onHireAt?: string;
  meterAtPickup?: string;
};

export type EndRentalHirePayload = {
  offRentAt?: string;
  /** SERV-3100: both optional, as the API now takes them. */
  offRentCallLog?: string;
  meterAtReturn?: string;
  returnCondition?: string;
  damageCharge?: number;
};

export type SettleRentalAgreementPayload = {
  invoiceNumber: string;
  invoiceAmount: number;
  depositReleased?: boolean;
  invoiceS3Keys?: string[];
};

/** One row of the On hire board, with its clock and projection already computed by the server. */
export type LiveRentalRow = {
  agreement: ProcurementRentalAgreement;
  clock: {
    daysLeft: number;
    overdue: boolean;
    dueSoon: boolean;
    accrued: string | null;
  } | null;
  projectedTotal: string | null;
  cheapestTotal: string | null;
  saving: string | null;
};

/**
 * SERV-2973: item types the PURCHASE returns flow must refuse.
 *
 * The return lifecycle on an item - RMA, restocking fee, EasyPost label, credit memo - models
 * sending a purchase back to a vendor for a refund. None of that describes handing rented kit back
 * to the branch it came from, where the question is when the meter stopped and there is no refund
 * to chase, so such a return could never legitimately settle.
 *
 * Mirrors the backend's `isReturnEligibleItemType` (procurement/types.ts), which is what actually
 * enforces it; this exists so the officer never sees the option rather than being refused after
 * filling the form in.
 *
 * Scoped to the equipment rental lane on purpose. Storage units and the vehicle-booking lane have
 * the same problem, but those screens are SERV-2974 and SERV-2976 and are in flight in parallel.
 */
export const RETURN_INELIGIBLE_ITEM_TYPES: readonly string[] = LANE_ITEM_TYPES.EQUIPMENT_RENTAL;

export const isReturnEligibleItemType = (itemType: string): boolean =>
  !RETURN_INELIGIBLE_ITEM_TYPES.includes(itemType);
